import type { ClarityStatus, HikeGrade, Interest, Lang, LocalProducerKind, Mode, OfferKind, Pace, TravelMode, UiLang, VehicleKind, Vibe, WinterAccessStatus, WinterActivityKind } from "./types";

/**
 * All UI-tekst, på begge språk.
 *
 * Samlet i én fil framfor spredt i komponentene: et nytt språk er én ny
 * oppføring her, og TypeScript sier fra hvis en nøkkel mangler.
 * Tekst som API-et genererer (advarsler, forklaringer, stedsbeskrivelser)
 * kommer allerede oversatt derfra — se api/src/services/messages.ts.
 */

export interface Dictionary {
  // Topptekst
  eyebrow: string;
  title: string;
  lede: string;
  /** Hintet nederst i toppseksjonen om at siden fortsetter. */
  scrollCue: string;
  /**
   * Om meg-seksjonen og meldingsskjemaet.
   *
   * Kravet var å kunne motta meldinger uten at e-postadressen står på
   * siden. Derfor et skjema og ikke en `mailto:` — den *er* å publisere
   * adressen, bare skrevet på en annen måte.
   */
  aboutTitle: string;
  /** Når meldingsskjemaet ikke er satt opp. Da står profillenka igjen. */
  /**
   * ── Baren nederst til høyre ────────────────────────────────────────────
   *
   * Ønsket som ble meldt: «en tilbakemeldingsbar nederst til høyre, med en
   * karusell, og spørsmålet skal dukke opp om personen har vært her lenge».
   *
   * `trust*` er de fem påstandene som roterer til ekte tilbakemeldinger
   * finnes. Alle fem er sjekket mot koden, ikke antatt — se data/feedback.ts,
   * som også sier hvorfor det ikke står oppdiktede anmeldelser der.
   */
  trustLive: string;
  trustStops: string;
  trustNoTracking: string;
  trustHonest: string;
  trustFree: string;
  feedbackAsk: string;
  feedbackAskBody: string;
  feedbackPlaceholder: string;
  feedbackNoThanks: string;
  feedbackClose: string;
  /**
   * ── Når en del av siden faller ut ──────────────────────────────────────
   *
   * Meldt fra bruk: «de planla en rute, og da de rullet ned til kartet krasjet
   * det liksom og lastet hjemskjermen på nytt».
   *
   * `sectionFailedRest` er den viktigste av de tre. Den som ser en feilmelding
   * antar at alt er ødelagt — og her er det nettopp ikke tilfellet. Se
   * components/ErrorBoundary.tsx.
   */
  sectionFailed: (part: string) => string;
  sectionFailedRest: string;
  sectionFailedRetry: string;
  /** Navnene på delene som kan falle ut hver for seg. */
  partMap: string;
  partPlan: string;
  partPanels: string;
  contactOff: string;
  aboutLede: (author: string) => string;
  aboutBody: string;
  contactMessageLabel: string;
  contactPlaceholder: string;
  contactNameLabel: string;
  contactReplyLabel: string;
  /** Hvorfor svaradressen er valgfri: et forslag trenger ikke et svar. */
  contactReplyHint: string;
  contactSend: string;
  contactSending: string;
  contactSent: string;
  contactFailed: string;
  contactTooShort: (min: number) => string;

  // Korridorvelger
  corridorLabel: string;
  corridorHint: string;
  longJourneys: string;
  longJourneysHint: string;
  helpMeChoose: string;
  helpMeChooseHint: string;
  interestsLabel: string;
  interestName: Record<Interest, string>;
  monthLabel: string;
  /**
   * Hva måneden avgjør.
   *
   * Her sto `helpMeChooseHint` — panelets ingress — under «Når reiser du?»,
   * altså et svar på et annet spørsmål.
   */
  monthHint: string;
  /**
   * «Når bør jeg komme?» — panelet som svarer med måneder.
   *
   * Alle fakta kommer som tallplasser. Ingenting bakes inn i strengen: en
   * oversettelse med et tall i ville vært en andre sannhet, og den ville
   * sluttet å stemme uten at noen så det.
   */
  monthAnswerTitle: string;
  monthAnswerHint: string;
  monthAnswerLead: string;
  monthAnswerNone: string;
  monthAnswerMatches: (months: string) => string;
  monthNoSeason: (names: string) => string;
  monthGiveMidnightSun: string;
  monthGiveAurora: (stops: number) => string;
  monthGiveAllOpen: string;
  monthGiveQuiet: (load: number) => string;
  monthGiveLongDays: (hours: number) => string;
  monthTakeClosures: (count: number, names: string) => string;
  monthTakeShortDays: (hours: number) => string;
  monthTakePolarNight: string;
  monthTakeBusy: (load: number) => string;
  monthTakeNoAurora: string;
  monthTakeNoMidnightSun: string;
  /** De faste forbeholdene. Står som tekst, ikke som en fotnote. */
  monthCaveatRain: string;
  monthCaveatWhere: string;
  monthCaveatNormals: string;
  /**
   * Interessene måneden gjør umulige.
   *
   * Vist som synlig tekst i knappen, ikke som et `title`: et `title` er
   * usynlig på berøring, og knappen er `aria-disabled` framfor `disabled`
   * nettopp for at den skal kunne svare når noen trykker på den.
   */
  interestOffSeason: (month: string) => string;
  interestOffSeasonWhy: (interest: string, month: string) => string;
  /** Sagt, ikke filtrert stille: valget står, men teller ikke med. */
  interestOffSeasonChosen: (names: string, month: string) => string;
  interestOffSeasonRemove: string;
  /**
   * Når det er fullt av folk på ruta.
   *
   * Samme regel som månedssvaret over: alle tall er plasser, ingen bakes inn.
   * «25 ganger» er regnet ut av SSB-tallene, og en oversettelse med tallet i
   * seg ville sluttet å stemme neste gang dataene hentes.
   */
  crowdTitle: string;
  crowdHint: string;
  crowdSummary: (peak: string, quiet: string, swing: number) => string;
  crowdFlat: (peak: string) => string;
  crowdBusiest: (region: string, swing: number) => string;
  crowdRegions: (names: string) => string;
  /** Hva tallene ikke teller. Står som tekst — se CrowdCalendar. */
  crowdCaveat: string;
  crowdPeakLabel: string;
  crowdQuietLabel: string;
  crowdBar: (month: string, load: number) => string;
  /**
   * Lenkene ned til de indekserbare sidene.
   *
   * Appen er én adresse; innholdet i den har 130 egne. Uten en lenke herfra er
   * de sidene noe bare et sitemap kjenner — og en side ingen lenker til er en
   * side søkemotorene regner som mindre viktig enn den er. Se
   * api/src/services/seo/.
   */
  browseTitle: string;
  browseTrips: string;
  browsePlaces: string;
  /**
   * Turen til ett sted, framfor turen langs en rute.
   *
   * For den som så én reel og bare vil dit. Alle tall er plasser, som
   * ellers — se `crowdTitle` for hvorfor. Se api/src/services/placeTrip.ts.
   */
  placeTripTitle: string;
  placeTripHint: string;
  placeTripLead: string;
  placeTripPick: string;
  placeTripFrom: string;
  placeTripAnywhere: string;
  placeTripDaysLabel: string;
  placeTripNeed: (days: number) => string;
  placeTripGetting: (from: string, hours: number, km: number) => string;
  placeTripNearbyTitle: string;
  placeTripNearby: (hours: number) => string;
  placeTripNothingNear: string;
  placeTripDay: (n: number) => string;
  placeTripOpenRoute: string;
  /**
   * En plan om ett sted, framfor om ruta stedet ligger på.
   *
   * Meldt fra bruk: «velger jeg ett stopp, hvorfor kan jeg ikke få en rute
   * bare for det? Jeg må planlegge hele ruta og fjerne de andre stoppene, og
   * det er veldig irriterende». Se `focusStopId`.
   */
  placeTripPlan: string;
  focusOnly: (place: string) => string;
  focusWhole: string;
  focusNote: (place: string) => string;
  /**
   * «Utforsk steder» — for den som ikke vet hva stedet heter.
   *
   * Rutevelgeren spør «hvor skal du?», og det forutsetter at man vet det.
   * Se components/ExplorePanel.tsx.
   */
  exploreTitle: string;
  exploreHint: string;
  exploreSearch: string;
  exploreAllRoutes: string;
  exploreCount: (n: number) => string;
  exploreNoMatch: string;
  exploreFamousFor: string;
  exploreBackground: string;
  exploreOnRoute: string;
  exploreWatch: string;
  exploreOpenPage: string;
  exploreClose: string;
  explorePlanHere: string;
  exploreHikeDay: (hours: number) => string;
  /**
   * Kjøretøyet som ikke er i sesong.
   *
   * Sagt, ikke filtrert bort — samme regel som `interestOffSeason`. En
   * motorsykkel i januar er ikke forbudt, den er upraktisk, og forskjellen er
   * brukerens å ta stilling til. Se `VEHICLE_SEASON` i services/winter.ts.
   */
  vehicleOffSeason: (month: string) => string;
  vehicleOffSeasonWhy: (vehicle: string, month: string) => string;
  monthName: string[];
  findRoutes: string;
  finding: string;
  recommendTitle: string;
  recommendPick: string;
  recommendMatch: (score: number) => string;
  recommendEmpty: string;
  recommendClose: string;
  stopCount: (n: number) => string;
  endToEnd: (hours: string) => string;

  // Kontrollpanel
  /** Overskrift og knapp på ruta som er valgt, vist i skjemaet. */
  /** Knappen på hvert rutekort som åpner presentasjonen. */
  previewRoute: string;
  chosenRoute: string;
  changeRoute: string;
  tripLabel: string;
  tripHint: string;
  daysLabel: string;
  /**
   * «Hvor reiser du fra?»
   *
   * Alle rutene begynner et sted i Norge, og appen antok at den reisende alt
   * sto der. Den som bor i Oslo og vil kjøre Lysefjorden har en etappe før
   * turen begynner som planen ikke visste om.
   */
  fromLabel: string;
  fromNone: string;
  fromHint: string;
  /**
   * ── Hjemreisen ─────────────────────────────────────────────────────────
   *
   * Meldt fra bruk: «hva med å komme tilbake? Om jeg må tilbake til Oslo og vil
   * ha en tur på fem dager, hvordan påvirker det planleggeren?»
   *
   * `returnHint` sier bare *at* det påvirker den, ikke hvor mye: timetallet
   * avhenger av hvilket stopp turen faktisk ender på, og det vet vi først når
   * planen finnes. Det ekte tallet kommer fra serveren som en advarsel.
   */
  returnLabel: string;
  returnNone: string;
  returnHint: string;
  /** Fanen og overskriften på dagen som går med til å reise hjem. */
  homewardTab: string;
  homewardTitle: (place: string) => string;
  /** Overskriften over etappen fram til der ruta begynner. */
  approachHeading: (place: string) => string;
  daysOption: (n: number) => string;
  /**
   * Stepperen ved siden av dagforslagene.
   *
   * Forslagene er fire tall utledet av rutas lengde. De er gode svar, men de
   * var også grensen, så åtte dager på en kort rute var uoppnåelig selv om
   * serveren tar imot alt fra 2 til 12.
   */
  daysFewer: string;
  daysMore: string;
  /** Sagt, ikke rettet stille: hva en lengde utenfor forslagene koster. */
  daysRoomy: (days: number) => string;
  daysTight: (days: number) => string;
  modeLabel: string;
  /**
   * Hvordan et sted *nås*. Et faktum om stedet.
   *
   * Skilt fra `travelModeName`, som er hva den reisende *valgte*. De var samme
   * tabell da valget og faktumet var samme type, og det var sammenblandingen
   * som lot et togønske komme ut som en bilplan. Se api/src/services/messages.ts.
   */
  modeName: Record<Mode, string>;
  /**
   * Reisemåten den reisende valgte. «Kollektivt» hører bare hjemme her.
   *
   * Ny påkrevd nøkkel med vilje: den feiler kompileringen i alle fem
   * ordbøkene til den er fylt ut, som er den eneste mekanismen som garanterer
   * at ingen leser en uoversatt modus-id.
   */
  travelModeName: Record<TravelMode, string>;
  modeUnavailableHint: (corridor: string) => string;
  /**
   * Det valgte dekker ikke hele ruta, så den blir kollektiv.
   *
   * Vist som synlig tekst under velgeren, ikke som `title` på en deaktivert
   * flis: en `title` vises aldri på en telefon, som er der de fleste
   * planlegger — og «deaktivert» sa dessuten «dette kan du ikke få», som ikke
   * er sant. Se api/src/services/messages.ts, `modeWidened`.
   */
  modeWidenedHint: (mode: string, corridor: string) => string;
  vibeLabel: string;
  vibeName: Record<Vibe, string>;
  paceLabel: string;
  paceName: Record<Pace, string>;
  paceDetail: Record<Pace, string>;
  dateLabel: string;
  dateHint: string;
  clearDate: string;
  weatherToggleTitle: string;
  weatherToggleOn: string;
  weatherToggleOff: string;
  bestDayToggleTitle: string;
  bestDayToggleOn: string;
  bestDayToggleOff: string;
  generate: string;
  generating: string;

  // Statistikk
  /** Dager — det foerste man lurer paa, og som ingen av stripene hadde. */
  /**
   * Avgangen man tar paa slutten av dagen, og veien videre.
   *
   * Meldt fra bruk: «toget jeg tar paa dag 2 fra et stopp paa dag 1 burde ogsaa
   * vises paa dag 1». Se `onward` i ItineraryBoard.
   */
  /** Sammendraget i hodet paa «turen i tall», saa doera ikke er lukket blindt. */
  summaryFoldSummary: (days: number, stops: number) => string;
  /**
   * «Greit aa vite» og «mer om stedet».
   *
   * Meldt fra bruk: «reiseplanen skal vise grunnleggende informasjon, med
   * resten som utvider seg hvis brukeren vil se den», og «i hver dag er det
   * mange stengte veier, saa jeg maa rulle helt til bunnen for aa se
   * reiseplanen min».
   */
  /** Sammendraget paa det som ligger bak folden i stedsskuffen. */
  stopExtrasSummary: string;
  dayInfoTitle: string;
  dayInfoSummary: (count: number) => string;
  stopMore: string;
  onwardTitle: (place: string) => string;
  onwardToDay: (day: number) => string;
  statDays: string;
  statDistance: string;
  statTravel: string;
  statHighest: string;
  statStops: string;
  unitKm: string;
  unitMasl: string;
  /**
   * Sjø og bading. Se api/src/services/ocean.ts.
   *
   * Avstanden til målepunktet er med i teksten og ikke bare i dataene, fordi
   * den avgjør hvor mye tallet er verdt: null kilometer er stedet man står,
   * seks kilometer er fjorden utenfor.
   */
  seaTitle: string;
  seaHere: string;
  seaAt: (km: number) => string;
  seaWaves: (metres: number) => string;
  seaSource: string;
  /** Lyttetips langs ruta. Se api/src/data/soundtrack.ts. */
  soundtrackTitle: string;
  soundtrackSubtitle: (hours: number) => string;
  soundtrackPodcast: string;
  soundtrackSearchNote: string;

  bathingKind: Record<"beach" | "swim" | "resort", string>;

  /** Fredete bygninger, fra Riksantikvaren. */
  heritageTitle: string;
  heritageDated: (dated: string) => string;
  heritageMore: string;
  heritageSource: string;

  /**
   * Klimanormaler 1991–2020. Ikke det samme som værvarselet, og skillet må
   * være tydelig i teksten: varselet gjelder dagen, normalen gjelder måneden.
   */
  climateTitle: string;
  climateRange: (min: number, max: number) => string;
  climateWetDays: string;
  climateClearNights: string;
  climateSnowDays: string;
  climateFrostNights: string;
  climateSource: string;

  /** Klesrådet, utledet fra normalene langs ruta. */
  packingTitle: string;
  packingWetNote: (percent: number) => string;
  packingSnowNote: string;
  /**
   * Panelet på skjermen — se components/PackingPanel.tsx. To bolker og en
   * søkeknapp per gjenstand.
   */
  packingClothing: string;
  packingGear: string;
  /** Etikett på selve søkeknappen (tittel/tooltip). */
  packingShop: string;
  /** Skjermleser-teksten for søkeknappen, med varen navngitt. */
  packingShopFor: (item: string) => string;
  /** Sier at knappene er søk, ikke en anbefaling — samme ærlighet som lyttetipsene. */
  packingShopHint: string;

  /** Hva det koster å kjøre ruta, i mengde og i kroner. */
  summaryRunningTitle: string;
  summaryFuelUnit: string;
  summaryElectricUnit: string;
  summaryTollUnit: string;
  summaryClimbNote: (ascentM: number, percent: number) => string;
  summaryTollNote: (schemes: number) => string;
  energySummary: (litres: number, kWh: number, ascentM: number) => string;
  tollCertainTitle: string;
  tollPossibleTitle: string;
  tollDiscountNote: (percent: number) => string;


  // Kart og profil
  mapTitle: string;
  mapSubtitle: string;
  map3dSubtitle: string;
  mapView3d: string;
  mapViewFlat: string;
  mapLoading: string;
  flyStart: string;
  flyStop: string;
  basemapTopo: string;
  basemapGrey: string;
  terrainTitle: string;
  terrainSubtitle: string;
  legendNotChosen: string;
  tagName: Record<VibeTagKey, string>;

  // Ruteresultat
  dayTab: (n: number) => string;
  travelSuffix: string;
  departuresHeading: string;
  /** Strekningen mellom to nabostopp. Se components/ConnectionRow.tsx. */
  connectionSummary: (km: number, duration: string, mode: string) => string;
  connectionWalkable: string;
  connectionTrailhead: string;
  /**
   * At kollektivtransport også dekker strekningen man kjører.
   *
   * Meldt fra bruk: den som velger bil fikk aldri vite at et tog følger den
   * samme dalen. En opplysning, ikke et forslag om å bytte reisemåte.
   */
  connectionAlsoBy: (mode: string) => string;
  connectionDirections: string;
  /**
   * Alternative reiser på samme strekning.
   *
   * Entur svarer med flere forslag på samme spørsmål, og appen ba lenge om
   * nøyaktig ett. En ferje som går sjeldnere og kutter to timer, lå usynlig.
   */
  journeyAlternatives: string;
  journeyTransfers: (count: number) => string;
  journeyDirect: string;
  journeyFastest: string;

  /**
   * Hvorfor dagen ble slik. Se components/DayRationaleNote.tsx.
   *
   * Ingen av dem sier at et enkeltstopp ble flyttet mellom to dager —
   * optimalisereren flytter dagsgrenser, ikke enkeltstopp, og teksten skal
   * ikke love en presisjon datamodellen ikke har.
   */
  rationaleTitle: string;
  rationaleUnchanged: string;
  rationaleClearAndViewHeavy: string;
  rationaleClear: string;
  rationaleMurky: string;
  rationaleRearranged: string;

  /** Hvorfor en vanlig reiseplan ryker. Se components/WhyItinerariesBreak.tsx. */
  whyTitle: string;
  whyLede: string;
  /** Seks kort, i samme rekkefølge som ikonene i komponenten. */
  whyCards: { title: string; body: string }[];

  /** Beskrankningene på én dag — fottur, ferje, stengt vei. Se components/DayConstraints.tsx. */
  constraintFullDay: string;
  constraintHikeOwnsDay: string;
  constraintDaylightFits: (hours: number) => string;
  constraintDaylightShort: (hours: number) => string;
  constraintFerry: string;
  constraintFerryMissed: (time: string) => string;
  constraintFerryNone: string;
  constraintOnTodaysLeg: (from: string, to: string) => string;

  /** Klimasjokk og lange tunneler på etappen. Se utils/routeAlerts.ts. */
  alertClimateTitle: string;
  alertClimateBody: (ascentM: number, topM: number, dropC: number) => string;
  alertTunnelTitle: (name: string, km: number) => string;
  alertTunnelBody: (road: string, minutes: number) => string;

  /** Sol og dagslys ved ankomst til et stopp. Se utils/sunlight.ts. */
  sunTitle: string;
  sunArrival: (time: string) => string;
  sunRise: string;
  sunSet: string;
  sunGolden: string;
  sunDaylight: string;
  sunAfterDark: string;
  sunMidnight: string;
  sunPolarNight: string;

  /** Sikten på et utsiktspunkt. Se api/src/services/clarity.ts. */
  clarityLabel: string;
  clarityStatus: Record<ClarityStatus, string>;
  clarityDetail: (fog: number, cloud: number) => string;

  /** Kjøretøyvelgeren og adkomstadvarslene. */
  vehicleLabel: string;
  vehicleHint: string;
  vehicleName: Record<VehicleKind, string>;
  vehicleWarning: string;
  vehicleNarrow: string;
  vehicleSteep: string;
  vehicleMaxLength: (metres: number) => string;

  /** Lokalmat og setrer. */
  producerBadge: string;
  producerTitle: string;
  producerKind: Record<LocalProducerKind, string>;
  producerSelfService: string;
  /** Lenketekst for et sted uten egen nettside. Se utils/placeLink.ts. */
  producerFind: string;

  fromStart: (place: string) => string;
  freeDay: string;
  viewIndex: string;
  viewIndexShort: string;
  weatherAgainstView: string;

  // Justering
  addStop: string;
  addStopNearby: (n: number) => string;
  addStopAll: (n: number) => string;
  showAll: (n: number) => string;
  removeStop: (name: string) => string;
  resetAdjustments: (n: number) => string;
  close: string;

  // Budsjett
  budgetTitle: string;
  budgetPerPerson: (n: number) => string;
  budgetRange: (low: string, high: string) => string;
  budgetTotal: string;
  budgetCurrency: string;
  budgetRateNote: (date: string) => string;
  budgetTravellers: string;

  // Nordlys
  auroraTitle: string;
  auroraChance: (percent: number) => string;
  auroraKp: (kp: number, needed: number) => string;
  auroraLimited: Record<"daylight" | "clouds" | "activity", string>;
  auroraGood: string;
  auroraSource: string;

  // Ski
  // Fottur
  hikeTitle: string;
  hikeRoundTrip: (hoursMin: number, hoursMax: number) => string;
  hikeDistance: (km: number) => string;
  hikeAscent: (m: number) => string;
  hikeGradeName: Record<HikeGrade, string>;
  hikeTrailhead: string;
  hikeSeason: string;
  hikeReachedBy: string;
  hikeGuidesTitle: string;
  hikeBookAhead: (days: number) => string;
  hikeGuidesNone: string;
  hikeBadge: string;

  // Trafikkmeldinger og veikameraer
  trafficTitle: string;
  trafficSubtitle: string;
  trafficCount: (n: number) => string;
  trafficBlocking: (n: number) => string;
  trafficSeverity: Record<"closed" | "convoy" | "warning" | "roadwork", string>;
  trafficMountainPass: string;
  /** Sier at meldingen ligger på strekningen man skal kjøre. */
  trafficOnRoute: string;
  trafficOffRoute: string;
  trafficFromRoad: (km: number) => string;
  trafficNear: (km: number) => string;
  trafficUntil: (date: string) => string;
  trafficSource: string;
  trafficCredit: string;
  copy: string;
  summaryTitle: string;
  summarySubtitle: string;
  summaryEyebrow: string;
  summaryClearHours: string;
  summaryHikeHours: string;
  /** «Anslag for 4 utsiktspunkter, regnet fra værsymbolet.» */
  summaryClearNote: (viewpoints: number) => string;
  summaryNoDate: string;
  summaryBest: (stopName: string) => string;
  summaryHighest: (stopName: string, metres: number) => string;
  summaryFooter: string;
  summaryDownload: string;
  summaryCopy: string;
  summarySaved: string;
  summaryCopied: string;
  roadbookDays: string;
  roadbookStops: string;
  roadbookFooter: string;
  /**
   * Plass til å skrive selv, per dag på papirutgaven.
   *
   * Det ene papir kan som skjermen ikke: en avgang som ble flyttet, navnet
   * på hytta man fikk anbefalt, hvor bilen står.
   */
  roadbookNotes: string;
  roadbookPrint: string;
  emergencyTitle: string;
  emergencySubtitle: string;
  /** Hvem som svarer på hvert nummer. */
  emergencyWho: {
    medical: string;
    police: string;
    fire: string;
    sea: string;
    doctor: string;
    road: string;
  };
  emergencyWhatTitle: string;
  /** Rekkefølgen på det man sier. Posisjon først — operatøren spør om det hver gang. */
  emergencySteps: { title: string; detail: string }[];
  emergencyFactsTitle: string;
  emergencyFacts: string[];
  emergencyPositionsTitle: string;
  emergencyPositionsHint: string;
  goldenBest: string;
  /** «kl. 19:42–21:15, 93 min med godt lys» */
  goldenWindow: (from: string, to: string, length: string) => string;
  goldenAllDay: (length: string) => string;
  goldenMidnightSun: (length: string) => string;
  goldenMorning: (from: string, to: string) => string;
  goldenPolarNight: string;
  goldenSunTooHigh: string;
  goldenMinutes: (minutes: number) => string;
  goldenHours: (hours: number) => string;
  goldenHoursMinutes: (hours: number, minutes: number) => string;
  /**
   * Holdeplassene i nærheten. Se api/src/services/lines.ts.
   *
   * `nearbyOffNetwork` og `nearbyNone` er *ikke* samme beskjed. Fem stopp har
   * koordinater utenfor gangnettet — Svartisen, Nigardsbreen, Hallingskarvet,
   * Dovrefjell, Lysefjord — og for dem er «ingen holdeplass i nærheten» en
   * påstand om stedet som ikke er sann. Vi vet bare ikke veien dit.
   *
   * `nearbyNote` sier «registrert nå» og aldri «ute av drift»: en 60-dagers
   * prøve ga null avganger for de døde radene, men Entur har ingen ruteplan et
   * år fram, så en sommerkai og en nedlagt er ikke til å skille.
   */
  nearbyTitle: (km: number) => string;
  nearbyAway: (km: number) => string;
  nearbyNone: (km: number) => string;
  nearbyOffNetwork: string;
  nearbyUnavailable: string;
  nearbyNote: string;
  linesTitle: string;
  linesSubtitle: string;
  linesLoading: string;
  linesFailed: string;
  linesNone: string;
  /** «omtrent hvert 30. minutt» — intervallet, rundet til noe man kan si høyt. */
  linesEvery: (minutes: number) => string;
  /** «4 avganger i døgnet». `atLeast` når taket per linje ble truffet. */
  linesPerDay: (count: number, atLeast: boolean) => string;
  linesMore: (count: number) => string;
  linesFewer: string;
  linesRealtime: string;
  linesCredit: string;
  camerasTitle: string;
  camerasTowards: string;
  camerasRefresh: string;
  camerasRefreshHint: string;
  camerasCredit: string;

  // Varsling
  notifyLabel: string;
  notifyHint: string;
  notifyOn: string;
  notifyOff: string;
  notifyBlocked: string;
  notifyUnsupported: string;
  notifyViewImprovedTitle: (stop: string) => string;
  notifyViewImprovedBody: (day: number, before: number, after: number) => string;
  notifyViewLostTitle: (stop: string) => string;
  notifyViewLostBody: (day: number, before: number, after: number) => string;
  notifyAuroraTitle: (stop: string) => string;
  notifyAuroraBody: (chance: number) => string;

  // Verneområder og allemannsretten
  protectedTitle: string;
  protectedKind: Record<string, string>;
  protectedRegulation: string;
  protectedFactSheet: string;
  protectedCredit: string;
  accessFire: string;
  accessTent: string;
  accessLeaveNoTrace: string;

  /** Sier at opplysningene kommer fra en lagret plan, ikke fra nå. */
  staleData: (when: string) => string;

  // Navigasjon og eksport
  navigateLabel: string;
  openGoogleMaps: string;
  openAppleMaps: string;
  exportGpx: string;
  exportGpxDay: string;

  // Naturfare og ferjer
  hazardKind: Record<"avalanche" | "flood" | "landslide", string>;
  hazardLevel: (level: number) => string;
  hazardSource: string;
  hazardCredit: string;
  ferryTitle: string;
  ferrySubtitle: string;
  ferryDuration: (minutes: number) => string;
  ferryCancelled: string;
  ferryCredit: string;

  // Rutepresentasjon
  showcaseEyebrow: string;
  showcasePlan: string;
  showcasePrevious: string;
  showcaseNext: string;
  showcaseHint: string;

  // Tjenestefilter
  filterLabel: string;
  filterAll: string;
  filterNone: string;
  placeKind: Record<string, string>;

  // Karttegn
  mapPlaces: string;
  mapPlacesHint: string;
  mapLegendTitle: string;
  mapLegend: Record<"lodging" | "camping" | "charging" | "food" | "ski" | "hike", string>;
  /** Overskriften over etappeikonene — se lib/vehicleGlyphs.ts. */
  mapLegendVehicleTitle: string;
  vehicleGlyphName: Record<"tog" | "buss" | "bobil" | "bil" | "fot", string>;

  skiTitle: string;
  skiLifts: (n: number) => string;
  skiDownhill: string;
  skiNordic: string;
  skiNone: string;

  // Overnatting
  lodgingTitle: string;
  lodgingKind: Record<"hotel" | "hostel" | "camping" | "cabin", string>;
  dntHut: string;
  noLodging: string;

  // Fasiliteter
  amenitiesTitle: string;
  charging: string;
  fuel: string;
  food: string;
  fastCharge: string;
  chargePoints: (n: number) => string;
  diesel: string;
  foodType: Record<"restaurant" | "cafe" | "fast_food", string>;
  noAmenities: string;

  // Beste avreisedag
  bestDayTitle: string;
  bestDayPickThis: string;
  bestDayCompare: string;
  daylightHours: (hours: number) => string;

  // Optimalisering
  optimizerApplied: string;
  optimizerConsidered: string;
  optimizerGain: (points: number) => string;
  optimizerSwapNote: string;

  // Detaljpanel
  elevation: string;
  travelFromStart: string;
  suggestedTime: string;
  viewDependent: string;
  reachedBy: string;
  daylight: string;
  coordinates: string;
  enturStop: string;
  whenItRains: string;
  seeOnMap: string;
  photoBy: string;

  /*
   * Stedet i bevegelse. Se utils/videoLink.ts for hvorfor det er et søk og
   * ikke en innebygd spiller: en spiller koster enten en API-nøkkel med kvote
   * eller sporingsinformasjonskapsler før noen har trykket play.
   */
  videoLink: string;
  videoSource: string;
  /** Landsnavnet som legges til søket, så «Bergen» ikke blir Bergen i Tyskland. */
  videoCountry: string;

  /*
   * Reserveoppslaget for et sted som har en nettside. Se utils/placeLink.ts:
   * vi kan ikke se om siden svarer, så begge veier står framme.
   */
  placeAlt: string;
  placeAltSearch: string;
  placeAltHint: string;
  offersTitle: string;
  offerKind: Record<OfferKind, string>;
  historyTitle: string;
  historySource: string;

  // Handlinger
  share: string;
  shared: string;
  calendar: string;
  print: string;

  // Offline
  offlineTitle: string;
  offlineBody: string;

  // Tilstander
  emptyTitle: string;
  emptyBody: string;
  apiUnreachable: string;
  genericError: string;
  clipboardBlocked: string;

  // Bunntekst
  footerSources: string;
  footerDisclaimer: string;
  languageLabel: string;
  /** Vises når grensesnittet er oversatt, men stedsbeskrivelsene ikke er. */
  contentInEnglish: string;
  themeLabel: string;
  themeName: Record<"light" | "dark" | "system", string>;

  /**
   * Fritekstplanlegging. Se services/ai/ i API-et og docs/AI.md.
   *
   * `aiUnderstood` og `aiAssumed` er de to som betyr noe. En modell som
   * feiltolker, lager et gyldig skjema med feil innhold, og forskjellen på en
   * hjelpsom funksjon og en villedende én er om brukeren får se hva som ble
   * forstått før planen lages.
   */
  aiTitle: string;
  aiIntro: string;
  aiPlaceholder: string;
  aiCta: string;
  aiBusy: string;
  aiUnderstood: string;
  aiAssumed: string;
  aiUnavailable: string;
  aiFieldName: Record<"corridorId" | "days" | "mode" | "vibe" | "pace", string>;
  /**
   * Eksempler over feltet, til å klikke på.
   *
   * Poenget er ikke inspirasjon — det er å vise formen på et spørsmål
   * modellen faktisk kan svare på: antall dager, hvordan man reiser, og hva
   * turen skal handle om. Uten dem er fritekstfeltet en tom boks, og en tom
   * boks inviterer til alt fra ett ord til en hel reisedagbok.
   */
  aiExamplesLabel: string;
  aiExamples: [string, string, string, string];
  /** Skillelinja mellom fritekst og interessevalget i «vet ikke hvor»-panelet. */
  aiOrPickInterests: string;

  // Månedene i kort form, brukt av vinter- og klimapanelene der en full
  // månedsliste ellers ville tatt over raden.
  monthShort: string[];

  /** «Legg til et stopp» — se components/AddStopPicker.tsx. */
  addStopHint: string;
  addStopDwell: (hours: number) => string;
  addStopHike: (hours: number) => string;
  addStopAbout: (name: string) => string;

  /**
   * Vinteren på et sted: hva som er stengt, og hva man gjør i stedet.
   * Se api/src/data/winter.ts og components/WinterPanel.tsx.
   */
  winterTitle: string;
  winterStatus: Record<WinterAccessStatus, string>;
  winterNotInSeason: string;
  winterSeason: (months: string) => string;
  winterDoing: (month: string) => string;
  winterKind: Record<WinterActivityKind, string>;
  winterFullDay: string;
  winterMonths: (months: string) => string;
  winterBookAhead: (days: number) => string;

  /**
   * Klimaanslaget som overtar utenfor varselhorisonten.
   * Se api/src/services/outlook.ts og components/OutlookPanel.tsx.
   */
  outlookTitle: string;
  outlookEstimate: string;
  outlookScore: string;
  outlookRange: string;
  outlookColdestAt: (place: string) => string;
  outlookWetDays: string;
  outlookAsSnow: string;
  outlookSource: (
    covered: number,
    total: number,
    horizon: number,
    extendedHorizon: number,
  ) => string;

  /**
   * Langtidsvarselet i sjiktet mellom de to horisontene, døgn 10–16.
   * Se api/src/services/extended.ts og components/ExtendedPanel.tsx.
   *
   * `extendedBadge` er bevisst et annet ord enn `outlookEstimate`. Dette *er*
   * et varsel — det varierer fra dag til dag — og å kalle det et anslag ville
   * gjort det til det samme som klimanormalen, som er det ene det ikke er.
   */
  extendedTitle: string;
  extendedBadge: string;
  extendedScore: string;
  extendedReach: string;
  extendedReachNote: (nearHorizon: number) => string;
  extendedGrid: string;
  extendedGridNote: (farthestKm: number) => string;
  extendedDays: string;
  /**
   * Delt i to framfor å ta `(covered, total)`.
   *
   * Setningen sier *differansen* — «1 ligger forbi modellen» — og ikke noen av
   * de to tallene den regnes av. Med den samlede signaturen kunne
   * i18n-vakten som sjekker at hver funksjon setter inn argumentene sine ikke
   * si noe om denne nøkkelen, og den vakten finnes for en reell feil: en
   * oversetter skriver om setningen og mister `${n}` på veien. To nøkler som
   * hver setter inn det de får, holder vakten hel.
   */
  extendedDaysAll: string;
  extendedDaysShort: (uncovered: number) => string;
  /** Merkelappen på måleren, der tallet kommer fra et langtidsvarsel. */
  extendedDialLabel: string;
  /** Tekstsvaret på det stolpen viser, for den som ikke ser grafikken. */
  extendedSpan: (daysOut: number, nearHorizon: number, horizon: number) => string;
  extendedSource: (model: string, refinesOn: string) => string;

  /**
   * Sidespalten og de sammenleggbare seksjonene. Se lib/fold.ts.
   *
   * Seksjonsnavnene er *grupper* og ikke gjentakelser av korttitlene under —
   * «Vær og timing» rommer varselet, beste avreisedag og optimaliseringen,
   * som alle svarer på når man skal reise.
   */
  /**
   * Det praktiske: hvor man kjøper billetten, betaler bommen, leier bilen.
   * Se components/TravelServices.tsx for hvorfor det er så få lenker.
   */
  servicesTitle: string;
  servicesTicketsNote: string;
  servicesTollsNote: string;
  servicesRoad: string;
  servicesRoadNote: string;
  /**
   * Utleielenka. Substantivet følger kjøretøyet, ikke bare stedet.
   *
   * Sto som «Leiebil i Bergen» også for den som planla en bobiltur. Appen har
   * kjent `VehicleKind` hele tiden; det var bare lenka som ikke spurte.
   */
  servicesRental: (place: string, noun: string) => string;
  /** Hva man leier, kort nok til å stå i en lenke. */
  rentalNoun: Record<VehicleKind, string>;
  servicesRentalNote: string;
  servicesFoot: string;

  /**
   * Avgangen man faktisk skal ta. Se components/ConnectionRow.tsx.
   *
   * `connectionFrom` navngir stasjonen avgangen går fra, som ikke alltid er
   * stoppets navn — toget til Flåm går fra Myrdal.
   */
  connectionFrom: (station: string) => string;
  connectionLater: string;

  planSideLabel: string;
  foldWeather: string;
  foldCost: string;
  foldShare: string;

  /**
   * Kostnadsdelingen: hva turen koster per person når flere deler bilen.
   *
   * Se `SPLIT_SEATS` i api/src/services/budget.ts. Panelet vises bare der noe
   * faktisk deles — på tog og buss er alt billetter, og da finnes det ingen
   * besparelse å love.
   */
  splitTitle: string;
  splitLead: (range: string) => string;
  splitSeats: (seats: number) => string;
  splitAlone: string;
  splitYours: string;
  splitNote: string;

  /**
   * Å dele turen med noen. Se lib/url.ts.
   *
   * Hele turen har alltid ligget i URL-en, så en plan har vært delbar hele
   * tiden — ingenting sa det. `tripOwnedBy` er den ene nøkkelen her som ikke
   * kan settes sammen av appen: eieform er ulik i hvert språk, og «Sushant's
   * tur» med engelsk apostrof i en norsk setning er feil på en måte som synes.
   */
  shareTitle: string;
  shareNameLabel: string;
  shareLinkLabel: string;
  shareNamePlaceholder: string;
  /** Knappen som åpner delingen. Lenka vises ikke før den må. */
  /** Knappen som gjør en mottatt tur om til din egen økt. */
  sharedEdit: string;
  shareStart: string;
  shareCopy: string;
  shareCopied: string;
  shareCopyFailed: string;
  shareHint: string;
  tripOwnedBy: (name: string) => string;
  sharedByLead: (name: string) => string;
  sharedByNote: string;
}

type VibeTagKey = "fjell" | "fjord" | "by";

const no: Dictionary = {
  eyebrow: "Reiseplanlegger",
  /*
   * Overskriften spurte «Hva har du lyst til å oppleve?», og det er spørsmålet
   * hundre andre reiseplanleggere stiller. Den nye sier hva denne gjør som de
   * ikke gjør: den planlegger rundt forholdene, ikke bare rundt severdighetene.
   */
  title: "Legg en norgestur som faktisk går opp",
  lede: "Vær, fjelloverganger, ferjer, dagslys, fotturer og transport — satt sammen til én plan som tar hensyn til forholdene, ikke bare til avstanden.",
  scrollCue: "Rull ned",
  aboutTitle: "Om siden",
  contactOff: "Meldingsskjemaet er ikke satt opp ennå. Ta gjerne kontakt via lenka over.",
  sectionFailed: (part) => `${part} lot seg ikke vise.`,
  sectionFailedRest: "Resten av turen står som den skal.",
  sectionFailedRetry: "Prøv denne delen på nytt",
  partMap: "Kartet",
  partPlan: "Reiseplanen",
  partPanels: "Denne delen",
  trustLive:
    "Vær fra MET, avganger fra Entur, veimeldinger fra Statens vegvesen, skredvarsel fra NVE.",
  trustStops: "118 steder langs ti ruter, med høyde, åpningstider og hvor lenge man blir.",
  trustNoTracking: "Ingen sporing, ingen annonser, ingen tredjepartsskript.",
  trustHonest:
    "Varsler lenger fram enn ni døgn er merket «anslag, ikke et varsel» — også når det er fint vær.",
  trustFree: "Gratis, og uten konto. Planen ligger i lenka, ikke hos oss.",
  feedbackAsk: "Ble planen til noe?",
  feedbackAskBody:
    "Én setning om hvordan det gikk hjelper de neste — særlig det som manglet.",
  feedbackPlaceholder: "Det som overrasket deg, eller det som ikke stemte ...",
  feedbackNoThanks: "Ikke nå",
  feedbackClose: "Lukk tilbakemeldingsbaren",
  aboutLede: (author) =>
    `Laget av ${author}. En reiseplanlegger som planlegger rundt forholdene — været, fjellovergangene, ferjene og dagslyset — framfor bare rundt severdighetene.`,
  aboutBody:
    "Har du et forslag, funnet noe som er feil, eller savner du et sted som burde vært med? Send en melding. Alt av vær, avganger og veimeldinger kommer fra MET, Entur, Statens vegvesen og NVE — men hvilke steder som er med, er et redaksjonelt valg, og det blir bedre av å bli sagt imot.",
  contactMessageLabel: "Melding",
  contactPlaceholder: "Skriv hva du tenker ...",
  contactNameLabel: "Navn (valgfritt)",
  contactReplyLabel: "E-post (valgfritt)",
  contactReplyHint: "Bare om du vil ha svar. Et forslag trenger ikke et.",
  contactSend: "Send melding",
  contactSending: "Sender ...",
  contactSent: "Takk — meldingen er sendt.",
  contactFailed: "Meldingen kom ikke fram. Prøv igjen om litt.",
  contactTooShort: (min) => `Skriv minst ${min} tegn, så det er noe å svare på.`,

  rationaleTitle: "Hvorfor denne dagen?",
  rationaleUnchanged:
    "Dagen står som den geografisk jevne inndelingen ga den — værvarselet ga ingen grunn til å flytte grensene her.",
  rationaleClearAndViewHeavy:
    "Dette er den klareste dagen i varselet, så de mest utsiktsavhengige stoppene er samlet her. Selve ruta er uendret.",
  rationaleClear:
    "Dette er den klareste dagen i varselet, og dagsgrensene er lagt etter det. Selve ruta er uendret.",
  rationaleMurky:
    "Dette er den gråeste dagen i varselet, så den er satt av til stoppene som tåler vær. Selve ruta er uendret.",
  rationaleRearranged:
    "Dagsgrensene er flyttet etter værvarselet, så de mest værfølsomme delene av ruta faller på de klareste dagene. Selve ruta er uendret.",

  whyTitle: "Hvorfor vanlige reiseplaner ryker",
  whyLede:
    "Det som velter en norgestur står sjelden i reisehåndboka. Dette er hva planleggeren tar hensyn til for deg.",
  whyCards: [
    {
      title: "Været tar utsikten",
      body: "Utsiktspunktene flyttes til de klareste dagene, og tåka hentes som eget tall fra MET — ikke bare et værsymbol.",
    },
    {
      title: "Fotturen tar dagen",
      body: "En tur på tolv timer får dagen for seg selv. De andre stoppene flyttes, og vi sier fra om dagslyset ikke rekker.",
    },
    {
      title: "Ferja venter ikke",
      body: "Avgangene hentes for dagen du faktisk skal over, og står på den dagen i planen — ikke i en liste nederst.",
    },
    {
      title: "Fjellovergangen kan være stengt",
      body: "Stengte veier og kolonnekjøring hentes fra Vegvesenet og knyttes til strekningen du kjører den dagen.",
    },
    {
      title: "Fjellet koster energi",
      body: "Høydemeterne regnes inn i drivstoff og strøm, sammen med bompengesatsene langs ruta.",
    },
    {
      title: "Dekningen forsvinner",
      body: "Planen lagres på telefonen med kart, nødnumre og koordinater, så den virker når nettet ikke gjør det.",
    },
  ],

  corridorLabel: "Hvilken reise?",
  corridorHint: "Sju strekninger, fra Bergensbanen til midnattssola",
  longJourneys: "Lange reiser",
  longJourneysHint: "Flere strekninger satt sammen — en uke eller mer gjennom landet.",
  helpMeChoose: "Vet du ikke hvor du skal?",
  helpMeChooseHint: "Si hva du har lyst til å oppleve, så foreslår vi en rute.",
  interestsLabel: "Hva er du ute etter?",
  interestName: {
    fjord: "Fjorder",
    fjell: "Fjell og fottur",
    by: "Byer og kultur",
    historie: "Historie",
    mat: "Mat og drikke",
    nordlys: "Nordlys",
    midnattssol: "Midnattssol",
    strand: "Strender",
    dyreliv: "Dyreliv",
    tog: "Selve togreisen",
    ski: "Ski",
  },
  monthLabel: "Når reiser du?",
  monthHint: "Måneden avgjør hva himmelen kan by på, ikke bare været",
  monthAnswerTitle: "Når bør du komme?",
  monthAnswerHint: "Si hva du vil oppleve, så sier vi hvilke måneder som gir det. Det finnes ingen beste måned — bare måneder som gir ulike ting.",
  monthAnswerLead: "Hver måned gir noe og tar noe. Velg hva du er ute etter.",
  monthAnswerNone: "Ingen måned gir alt du valgte. Nordlys og midnattssol utelukker hverandre — du må velge.",
  monthAnswerMatches: (months) => `Kom i ${months}.`,
  monthNoSeason: (names) => `Måneden avgjør ikke ${names}. Fonna og Galdhøpiggen er sommerskisentre, og hvalsafarien utenfor Andenes går om vinteren.`,
  monthGiveMidnightSun: "Midnattssol nord for polarsirkelen",
  monthGiveAurora: (stops) => `Nordlys — ${stops} steder har det som et tilbud`,
  monthGiveAllOpen: "Alle 118 stoppene er åpne",
  monthGiveQuiet: (load) => `Rolig — ${load} % av det travleste ruta blir`,
  monthGiveLongDays: (hours) => `Lange dager — ${hours} timer lyst i Tromsø`,
  monthTakeClosures: (count, names) => `${count} stopp stengt, blant dem ${names}`,
  monthTakeShortDays: (hours) => `Korte dager — ${hours} timer lyst i Tromsø`,
  monthTakePolarNight: "Mørketid i nord — sola står ikke opp",
  monthTakeBusy: (load) => `Fullt — ${load} % av det travleste ruta blir`,
  monthTakeNoAurora: "Ikke nordlys — natta blir ikke mørk nok",
  monthTakeNoMidnightSun: "Ikke midnattssol",
  monthCaveatRain: "Regnet avgjør ikke valget: andelen våte dager ligger mellom 44 og 58 % hver eneste måned. I Oslo og Geiranger er juli og august de våteste.",
  monthCaveatWhere: "Hvor du er betyr mer enn når. Forskjellen mellom stedene i én måned er større enn forskjellen mellom månedene.",
  monthCaveatNormals: "Temperatur og nedbør er normaler for 1991—2020. De sier hva en måned pleier å være, ikke hva en bestemt uke blir.",
  interestOffSeason: (month) => `ikke i ${month}`,
  interestOffSeasonWhy: (interest, month) =>
    `${interest} finnes ikke i ${month} noe sted i Norge — det er astronomi, ikke vær. Velg en annen måned, eller noe annet å se etter.`,
  interestOffSeasonChosen: (names, month) =>
    `${names} finnes ikke i ${month}, og teller ikke med i forslaget.`,
  interestOffSeasonRemove: "Fjern",
  crowdTitle: "Når det er fullt av folk",
  crowdHint: "Ankomne gjester per måned langs ruta, som andel av rutas egen travleste måned. Tallene er SSBs, snittet over sju hele år.",
  crowdSummary: (peak, quiet, swing) => `${peak} er travlest. ${quiet} er roligst — ${swing} ganger færre gjester.`,
  crowdFlat: (peak) => `${peak} er travlest, men ruta har gjester hele året.`,
  crowdBusiest: (region, swing) => `Størst utslag: ${region}, ${swing} ganger mellom topp og bunn.`,
  crowdRegions: (names) => `Målt i ${names}.`,
  crowdCaveat: "Tallene teller dem som overnatter. Dagsturister og cruisepassasjerer er ikke med — og det er nettopp de som fyller kaia i Geiranger.",
  crowdPeakLabel: "travlest",
  crowdQuietLabel: "roligst",
  crowdBar: (month, load) => `${month}: ${load} % av det travleste ruta blir`,
  browseTitle: "Bla gjennom",
  browseTrips: "Alle rutene",
  browsePlaces: "Alle stedene",
  placeTripTitle: "Vil du bare til ett sted?",
  placeTripHint: "Ikke alle skal kjøre hele veien. Velg stedet du så, så sier vi hvor lang tid det tar, hvordan du kommer deg dit, og hva som ligger innen en dagstur.",
  placeTripLead: "Velg et sted, så regner vi ut resten.",
  placeTripPick: "Hvor vil du?",
  placeTripFrom: "Hvor lander du?",
  placeTripAnywhere: "Vet ikke ennå",
  placeTripDaysLabel: "Hvor mange døgn?",
  placeTripNeed: (days) => `Stedet krever minst ${days} døgn.`,
  placeTripGetting: (from, hours, km) => `Fra ${from}: rundt ${hours} t, ${km} km i luftlinje.`,
  placeTripNearbyTitle: "Innen en dagstur",
  placeTripNearby: (hours) => `${hours} t unna`,
  placeTripNothingNear: "Stedet ligger for seg selv — det er ingen andre stopp innen en dagstur.",
  placeTripDay: (n) => `Dag ${n}`,
  placeTripOpenRoute: "Se hele ruta dette stedet ligger på →",
  placeTripPlan: "Lag hele planen for dette stedet →",
  focusOnly: (place) => `Bare ${place}`,
  focusWhole: "Hele ruta igjen",
  focusNote: (place) =>
    `Denne planen handler om ${place} og det som ligger innen dagsturavstand — ikke om hele ruta.`,
  exploreTitle: "Utforsk steder",
  exploreHint: "Vet du ikke hvor du skal? Bla gjennom alle stedene. Trykk på et bilde for å se hva stedet er, hva det er kjent for, og hvilken rute det ligger på.",
  exploreSearch: "Søk etter et sted",
  exploreAllRoutes: "Alle rutene",
  exploreCount: (n) => `${n} steder`,
  exploreNoMatch: "Ingen steder passer søket. Prøv et annet ord, eller velg alle rutene.",
  exploreFamousFor: "Kjent for",
  exploreBackground: "Bakgrunn",
  exploreOnRoute: "Ligger på",
  exploreWatch: "Se video",
  exploreOpenPage: "Egen side om stedet",
  exploreClose: "Lukk",
  explorePlanHere: "Planlegg en tur innom her",
  exploreHikeDay: (hours) => `Fottur — sett av ${hours} timer.`,
  vehicleOffSeason: (month) => `Sjelden i ${month}`,
  vehicleOffSeasonWhy: (vehicle, month) =>
    `${vehicle} er lite brukt i ${month}: fjellovergangene ruta går over er stengt, og vinterdekk til motorsykkel finnes knapt. Valget står, men planen regner med sommerføre.`,
  monthName: [
    "Januar", "Februar", "Mars", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Desember",
  ],
  findRoutes: "Finn ruten min →",
  finding: "Vurderer rutene…",
  recommendTitle: "Dette passer best",
  recommendPick: "Velg denne",
  recommendMatch: (score) => `${score} % treff`,
  recommendEmpty: "Velg minst én ting du har lyst til å oppleve.",
  recommendClose: "Lukk forslagene",
  stopCount: (n) => `${n} steder`,
  endToEnd: (hours) => `${hours} totalt`,

  previewRoute: "Se ruta",
  chosenRoute: "Valgt rute",
  changeRoute: "Bytt rute",
  tripLabel: "Hvordan vil du reise?",
  tripHint: "Lengde, reisemåte og tempo — alt kan endres etterpå",
  daysLabel: "Antall dager",
  fromLabel: "Hvor reiser du fra?",
  fromNone: "Jeg starter der ruta starter",
  fromHint: "Vi legger til etappen fram dit, med avganger der de finnes",
  returnLabel: "Og hjem igjen?",
  returnNone: "Jeg sier ikke noe om hjemreisen",
  returnHint:
    "Skal du tilbake et sted, tar reisen tid av turen. Går det med en dag, sier planen hvilken.",
  homewardTab: "Hjem",
  homewardTitle: (place) => `Hjem til ${place}`,
  approachHeading: (place) => `Fram til ${place}`,
  daysOption: (n) => `${n} dager`,
  daysFewer: "Færre dager",
  daysMore: "Flere dager",
  daysRoomy: (days) =>
    `${days} dager er romsligere enn denne ruta trenger. Dagene blir korte, med god tid på hvert sted — det er et fint valg om du vil ha ro, og for mye om du vil ha framdrift.`,
  daysTight: (days) =>
    `${days} dager på denne ruta blir mest transport. Det går an, men regn med lange etapper og få stopp underveis.`,
  modeLabel: "Reisemåte",
  modeName: { tog: "Tog", bil: "Bil", buss: "Buss", fottur: "Fottur" },
  travelModeName: {
    kollektiv: "Kollektivt",
    tog: "Bare tog",
    buss: "Bare buss",
    bil: "Bil",
  },
  modeUnavailableHint: (corridor) => `Denne reisemåten går ikke hele veien på ${corridor}.`,
  modeWidenedHint: (mode, corridor) =>
    `${mode} går ikke hele veien på ${corridor}. Ruta blir lagt opp kollektivt — med toget der det går, og buss eller båt videre.`,
  vibeLabel: "Hva har du lyst til å oppleve?",
  vibeName: { fjell: "Fjell", fjord: "Fjord", by: "By", miks: "Litt av alt" },
  paceLabel: "Tempo",
  paceName: { avslappet: "Avslappet", aktiv: "Se mest mulig" },
  paceDetail: { avslappet: "1 stopp/dag", aktiv: "2 stopp/dag" },
  dateLabel: "Avreisedato",
  dateHint: "Valgfritt — gir vær, dagslys, nordlysvarsel og faktiske avganger",
  clearDate: "Fjern dato",
  weatherToggleTitle: "Legg ruten etter værvinduet",
  weatherToggleOn:
    "Henter varselet for hvert stopp på hver dato og flytter dagsgrensene slik at utsiktspunktene havner på de klare dagene.",
  weatherToggleOff: "Velg en avreisedato først — vi trenger å vite hvilke dager vi skal hente varsel for.",
  bestDayToggleTitle: "Foreslå beste avreisedag",
  bestDayToggleOn:
    "Scorer hele turen for hver mulige startdato i varselvinduet og sier hvilken som gir mest å se.",
  bestDayToggleOff: "Krever avreisedato — vi regner ni døgn framover fra den.",
  generate: "Lag ruteforslag →",
  generating: "Henter vær og avganger…",

  statDistance: "Luftlinje",
  statDays: "Dager",
  onwardTitle: (place) => `Videre til ${place}`,
  dayInfoTitle: "Greit å vite",
  stopExtrasSummary: "Opplevelser, overnatting, historie og kamera",
  dayInfoSummary: (count) => `${count} ting om denne dagen`,
  stopMore: "Mer om stedet →",
  summaryFoldSummary: (days, stops) => `${days} dager · ${stops} stopp · som bilde`,
  onwardToDay: (day) => `Dag ${day} →`,
  statTravel: "Reisetid",
  statHighest: "Høyeste punkt",
  statStops: "Stopp",
  unitKm: "km",
  unitMasl: "moh",
  seaTitle: "Sjø og bading",
  seaHere: "Målt der du står",
  seaAt: (km) => `Målt ${km} km unna`,
  seaWaves: (metres) => `bølger ${metres} m`,
  seaSource: "MET havvarsel, oppdatert i dag",
  soundtrackTitle: "Noe å høre på",
  soundtrackSubtitle: (hours) =>
    `${hours} timer reise. Musikk og podkast med en faktisk tilknytning til ruta.`,
  soundtrackPodcast: "podkast",
  soundtrackSearchNote:
    "Lenkene søker opp artisten — appen lager ingen spilleliste og ber ikke om tilgang til kontoen din.",

  bathingKind: { beach: "strand", swim: "badeplass", resort: "badeanlegg" },

  heritageTitle: "Fredete bygninger",
  heritageDated: (dated) => `datert ${dated}`,
  heritageMore: "Se i Kulturminnesøk",
  heritageSource: "Riksantikvaren, Askeladden",

  climateTitle: "Normalt i denne måneden",
  climateRange: (min, max) => `${min} til ${max} °C`,
  climateWetDays: "døgn med nedbør",
  climateClearNights: "klare netter",
  climateSnowDays: "døgn med snø",
  climateFrostNights: "netter med frost",
  climateSource: "Normaler 1991–2020, ERA5. Ikke et varsel — en middelverdi over tretti år.",

  packingTitle: "Hva du trenger med",
  packingWetNote: (percent) => `Det regner omtrent ${percent} % av døgnene i denne måneden.`,
  packingSnowNote: "Noen av stoppene har normalt snø.",
  packingClothing: "Klær",
  packingGear: "Annet",
  packingShop: "Søk etter hvor du kan kjøpe dette",
  packingShopFor: (item) => `Søk etter hvor du kan kjøpe ${item}`,
  packingShopHint:
    "Knappene er søk, ikke en anbefaling — appen har ikke sjekket noen butikk og tjener ingenting på hvor du handler.",

  summaryRunningTitle: "Å kjøre ruta",
  summaryFuelUnit: "liter bensin",
  summaryElectricUnit: "kWh strøm",
  summaryTollUnit: "kr bom",
  summaryClimbNote: (ascentM, percent) =>
    `${ascentM} høydemeter er regnet med, og de står for ${percent} % av forbruket.`,
  summaryTollNote: (schemes) =>
    schemes === 1
      ? "Ett bompengeanlegg langs ruta, med sats fra NVDB."
      : `${schemes} bompengeanlegg langs ruta, med satser fra NVDB. Hvilke du passerer avhenger av veivalget.`,
  energySummary: (litres, kWh, ascentM) =>
    `Rundt ${litres} liter bensin eller ${kWh} kWh strøm, medregnet ${ascentM} høydemeter — de koster uansett hvor pent du kjører.`,
  tollCertainTitle: "Betaler du uansett",
  tollPossibleTitle: "Avhenger av veivalget",
  tollDiscountNote: (percent) =>
    `Satsene er «takst liten bil» fra NVDB, med ${percent} % brikkerabatt. Elbil betaler mindre i mange anlegg.`,


  mapTitle: "Ruten langs korridoren",
  mapSubtitle: "Tegnet fra stoppenes faktiske koordinater. Hold over et punkt for bilde og detaljer.",
  map3dSubtitle:
    "Kartverkets topografiske kart lagt over ekte høydedata. Dra for å snurre, hold Ctrl mens du drar for å vippe kameraet.",
  mapView3d: "3D-terreng",
  mapViewFlat: "Skjematisk",
  mapLoading: "Laster terreng…",
  flyStart: "Fly ruten",
  flyStop: "Stopp",
  basemapTopo: "Topografisk",
  basemapGrey: "Gråtone",
  terrainTitle: "Terrengprofil",
  terrainSubtitle: "Høyden langs ruten, sett fra siden. Klikk en topp for detaljer.",
  legendNotChosen: "ikke valgt",
  tagName: { fjell: "fjell", fjord: "fjord", by: "by" },

  dayTab: (n) => `DAG ${n}`,
  travelSuffix: "reise",
  departuresHeading: "Avganger fra Entur",
  connectionSummary: (km, duration, mode) => `${km} km i luftlinje · ca. ${duration} med ${mode}`,
  connectionWalkable: "Gangavstand",
  connectionTrailhead: "Siste strekning til fots",
  connectionAlsoBy: (mode) => `Det går også ${mode} her`,
  connectionDirections: "Veibeskrivelse",
  journeyAlternatives: "Andre veier",
  journeyTransfers: (n) => (n === 1 ? "1 bytte" : `${n} bytter`),
  journeyDirect: "uten bytte",
  journeyFastest: "raskest",

  constraintFullDay: "Hele dagen",
  constraintHikeOwnsDay:
    "Turen legger beslag på dagen, så vi har flyttet de andre stoppene til nabodagene.",
  constraintDaylightFits: (hours) => `${hours} timer dagslys — turen rekkes i lyset`,
  constraintDaylightShort: (hours) => `Bare ${hours} timer dagslys — siste del går i mørket`,
  constraintFerry: "Ferje",
  constraintFerryMissed: (time) =>
    `Rekker du ikke ${time}, henger resten av dagen på neste avgang.`,
  constraintFerryNone: "Ingen avganger igjen denne dagen — planlegg overfarten på nytt.",
  constraintOnTodaysLeg: (from, to) => `Ligger på strekningen ${from}–${to}, som du kjører i dag.`,

  alertClimateTitle: "Kle deg for fjellet",
  alertClimateBody: (ascentM, topM, dropC) =>
    `Etappen stiger ${ascentM} meter, opp til ${topM} moh. Regn med rundt ${dropC} °C kaldere der oppe enn der du står nå.`,
  alertTunnelTitle: (name, km) => `${name} — ${km} km tunnel`,
  alertTunnelBody: (road, minutes) => `På ${road}, omtrent ${minutes} minutter under fjellet.`,

  sunTitle: "Sol og dagslys",
  sunArrival: (time) => `Framme rundt ${time}`,
  sunRise: "Soloppgang",
  sunSet: "Solnedgang",
  sunGolden: "Beste fotolys",
  sunDaylight: "Dagslys når du kommer",
  sunAfterDark: "Du er framme etter solnedgang — ta med hodelykt",
  sunMidnight: "Midnattssol — lyst hele døgnet",
  sunPolarNight: "Mørketid — sola står ikke opp",

  clarityLabel: "Sikt",
  clarityStatus: {
    klar: "Klar utsikt",
    delvis: "Delvis tåke eller skyet",
    tett: "Tett tåke — begrenset utsikt",
  },
  clarityDetail: (fog, cloud) => `${fog} % tåke, ${cloud} % skydekke`,

  vehicleLabel: "Hva kjører du?",
  vehicleHint: "Styrer bare hva planen advarer om — ikke hvilke stopp du får",
  vehicleName: {
    bil: "Vanlig bil",
    bobil: "Bobil eller campingvogn",
    motorsykkel: "Motorsykkel",
  },
  vehicleWarning: "Krevende for store kjøretøy",
  vehicleNarrow: "Enfeltsvei med møteplasser",
  vehicleSteep: "Bratt stigning",
  vehicleMaxLength: (metres) => `Lengdegrense ${metres} m`,

  producerBadge: "Lokal opplevelse",
  producerTitle: "Lokalmat og seter",
  producerKind: {
    sider: "Sideri",
    frukt: "Fruktbod",
    seter: "Seter",
    gardsutsalg: "Gårdsutsalg",
  },
  producerSelfService: "Selvbetjent — ta med kontanter eller Vipps",
  producerFind: "finn på kartet",
  fromStart: (place) => `fra ${place}`,
  freeDay: "Fri dag — ingen fastlagt reise. Utforsk i eget tempo.",
  viewIndex: "Utsiktsindeks",
  viewIndexShort: "utsikt",
  weatherAgainstView: "Været taler mot utsikten her.",

  addStop: "Legg til stopp",
  addStopNearby: (n) => `${n} stopp langs denne strekningen`,
  addStopAll: (n) => `Alle ${n} ledige stopp`,
  showAll: (n) => `Vis alle ${n} ledige stopp`,
  removeStop: (name) => `Fjern ${name} fra ruten`,
  resetAdjustments: (n) => `Nullstill ${n} ${n === 1 ? "endring" : "endringer"}`,
  close: "Lukk",

  budgetTitle: "Hva koster turen?",
  budgetPerPerson: (n) => `For ${n} personer`,
  budgetRange: (low, high) => `${low}–${high}`,
  budgetTotal: "Til sammen",
  budgetCurrency: "Valuta",
  budgetRateNote: (date) => `Kurs fra Norges Bank, ${date}.`,
  budgetTravellers: "Reisende",

  auroraTitle: "Nordlys i natt",
  auroraChance: (percent) => `${percent} % sjanse`,
  auroraKp: (kp, needed) => `Kp ${kp} — her trengs Kp ${needed}`,
  // Feltet sier hva som begrenser mest, ikke hva som er umulig — teksten må
  // lese riktig både ved 5 % og ved 45 %.
  auroraLimited: {
    daylight: "Mørket begrenser mest — nettene er ennå ikke lange nok her.",
    clouds: "Skydekket begrenser mest. Klarere vær innover i landet kan hjelpe.",
    activity: "Aktiviteten begrenser mest — Kp må opp for at nordlyset skal nå hit.",
  },
  auroraGood: "Alt ligger til rette: mørkt, klart og nok aktivitet.",
  auroraSource: "NOAA Space Weather Prediction Center",

  hikeTitle: "Siste etappe til fots",
  hikeRoundTrip: (hoursMin, hoursMax) =>
    `${hoursMin === hoursMax ? hoursMin : `${hoursMin}–${hoursMax}`} timer tur–retur`,
  hikeDistance: (km) => `${km} km`,
  hikeAscent: (m) => `${m} høydemeter`,
  hikeGradeName: {
    enkel: "enkel",
    moderat: "moderat",
    krevende: "krevende",
    ekspert: "ekspert",
  },
  hikeTrailhead: "Turen starter",
  hikeSeason: "Sesong",
  hikeReachedBy: "Startpunktet nås med",
  hikeGuidesTitle: "Guidede turer",
  hikeBookAhead: (days) => `bestill ${days} ${days === 1 ? "dag" : "dager"} før`,
  hikeGuidesNone: "Turen er merket og går uten guide i sesongen.",
  hikeBadge: "Fottur",

  trafficTitle: "På veien nå",
  trafficSubtitle:
    "Aktive meldinger fra Statens vegvesen innen 30 km av ruta. Stengte veier og kolonnekjøring først.",
  trafficCount: (n) => `${n} ${n === 1 ? "melding" : "meldinger"}`,
  trafficBlocking: (n) => `${n} ${n === 1 ? "sperring" : "sperringer"}`,
  trafficSeverity: {
    closed: "stengt",
    convoy: "kolonnekjøring",
    warning: "advarsel",
    roadwork: "veiarbeid",
  },
  trafficMountainPass: "fjellovergang",
  trafficOnRoute: "— strekningen du kjører",
  trafficOffRoute: "I nærheten, ikke på ruta",
  trafficFromRoad: (km) => `${km} km fra strekningen`,
  trafficNear: (km) => `${km} km fra ruta`,
  trafficUntil: (date) => `til ${date}`,
  trafficSource: "Vegvesenet",
  trafficCredit: "Trafikkmeldinger fra Statens vegvesen (NLOD). Oppdateres hvert femte minutt.",
  copy: "Kopier",
  summaryTitle: "Reisen i tall",
  summarySubtitle: "Et kort du kan laste ned eller lime inn i en samtale.",
  summaryEyebrow: "Reiseplan for Norge",
  summaryClearHours: "t klarvær",
  summaryHikeHours: "t til fots",
  summaryClearNote: (viewpoints) =>
    `Anslag for ${viewpoints} ${viewpoints === 1 ? "utsiktspunkt" : "utsiktspunkter"}: planlagt tid der ganget med hvor mye klarvær værsymbolet fra MET tilsier. Et anslag, ikke et løfte.`,
  summaryNoDate: "Velg en dato for å få anslag på hvor mye klarvær utsiktspunktene får.",
  summaryBest: (stopName) => `Beste forhold: ${stopName}`,
  summaryHighest: (stopName, metres) => `Høyeste punkt: ${stopName}, ${metres} moh`,
  summaryFooter: "reise-planer · vær fra MET Norway",
  summaryDownload: "Last ned bilde",
  summaryCopy: "Kopier bilde",
  summarySaved: "Lastet ned",
  summaryCopied: "Kopiert",
  roadbookDays: "dager",
  roadbookStops: "stopp",
  roadbookNotes: "Egne notater",
  roadbookFooter:
    "Laget med Reise-planer. Avganger fra Entur, vær fra MET Norway, veiforhold fra Statens vegvesen, kart fra Kartverket. Tidene er anslag — sjekk avganger og veistatus før du drar.",
  roadbookPrint: "Skriv ut / lagre som PDF",
  emergencyTitle: "Nødnumre i Norge",
  emergencySubtitle:
    "Ring gratis fra enhver mobil, også uten SIM-kort og uten dekning fra ditt eget selskap.",
  emergencyWho: {
    medical: "Medisinsk nødhjelp — ambulanse, luftambulanse",
    police: "Politi — også fjellredning og savnede personer",
    fire: "Brann — og akutt forurensning",
    sea: "Sjøredning — Hovedredningssentralen, ulykker på sjø og i fjord",
    doctor: "Legevakt — sykdom og skade som ikke er akutt livstruende",
    road: "Veimelding — Vegtrafikksentralen, stengte veier og kolonne",
  },
  emergencyWhatTitle: "Hva du skal si, i denne rekkefølgen",
  emergencySteps: [
    {
      title: "Hvor du er",
      detail:
        "Dette spørres om først, hver gang. Les opp koordinatene under, eller si veinummer og nærmeste kjente sted og kommune. Hjelp som ikke vet hvor den skal, er ingen hjelp.",
    },
    {
      title: "Hva som har skjedd",
      detail: "Kort: fall, trafikkulykke, bevisstløs, savnet. Ikke forklar mer enn det som spørres om.",
    },
    {
      title: "Hvor mange, og hvordan de har det",
      detail: "Puster de? Er de våkne? Store blødninger? Dette avgjør hva som sendes ut.",
    },
    {
      title: "Hvem du er, og nummeret du ringer fra",
      detail: "De ringer tilbake om samtalen brytes, og i fjellet brytes den ofte.",
    },
    {
      title: "Ikke legg på",
      detail:
        "Operatøren veileder deg mens hjelpen er på vei — også i hjerte-lungeredning. Bli på linja til du får beskjed om noe annet.",
    },
  ],
  emergencyFactsTitle: "Verdt å vite på disse rutene",
  emergencyFacts: [
    "Nødanrop går gjennom hvilket som helst nett som finnes der du står, ikke bare ditt eget selskaps. Har telefonen «ingen dekning», kan 113 likevel gå gjennom — prøv.",
    "Appen «Hjelp 113» fra Norsk Luftambulanse sender posisjonen din automatisk. Last den ned før turen, ikke under.",
    "Flere av stoppene på disse rutene har ingen mobildekning i det hele tatt — Hardangervidda, Trolltunga, Kjerag, Romsdalseggen. Avtal når du er ventet tilbake, med noen som vil savne deg.",
    "Er du hørselshemmet, send SMS til 1412. Det er nødnummeret for tekstmelding.",
    "På fjellet koordinerer politiet (112) redningen med Røde Kors, Norsk Folkehjelp og luftambulanse. Ring 112, ikke enkeltgruppene.",
  ],
  emergencyPositionsTitle: "Posisjonene på ruta, til å lese opp",
  emergencyPositionsHint:
    "Desimalgrader — formatet nødetatene ber om. Si «nord» før det første tallet og «øst» før det andre.",
  goldenBest: "Beste fotolys kl.",
  goldenWindow: (from, to, length) => `${from}–${to} · ${length} med lavt, varmt lys`,
  goldenAllDay: (length) =>
    `Sola kommer aldri høyere enn 6°, så hele lysvinduet på ${length} er gyllent`,
  goldenMidnightSun: (length) => `Midnattssol som ligger lavt — ${length} med gyllent lys`,
  goldenMorning: (from, to) => `Morgenlys ${from}–${to}`,
  goldenPolarNight: "Mørketid: ingen sol over horisonten, og ikke lys nok å fotografere i.",
  goldenSunTooHigh:
    "Høy midnattssol: sola faller aldri under 6°, så lyset er hardt hele døgnet.",
  goldenMinutes: (minutes) => `${minutes} min`,
  goldenHours: (hours) => `${hours} t`,
  goldenHoursMinutes: (hours, minutes) => `${hours} t ${minutes} min`,
  linesTitle: "Tog og buss herfra",
  linesSubtitle: "Hva som går forbi det neste døgnet, og hvor ofte.",
  linesLoading: "Henter ruteplanen …",
  linesFailed: "Fikk ikke ruteplanen fra Entur nå. Prøv igjen om litt.",
  linesNone: "Ingen avganger registrert her det neste døgnet.",
  nearbyTitle: (km) => `Holdeplasser innen ${km} km`,
  nearbyAway: (km) => `${km} km`,
  nearbyNone: (km) => `Ingen holdeplass innen ${km} km i luftlinje.`,
  nearbyOffNetwork: "Ingen gangveg herfra — vi finner ikke veien til en holdeplass fra denne koordinaten.",
  nearbyUnavailable: "Fikk ikke svar på hva som ligger i nærheten.",
  nearbyNote:
    "Nærmeste holdeplasser i luftlinje. Noen har ingen avganger registrert nå — en sommerkai og en nedlagt ser like ut i ruteplanen.",
  linesEvery: (minutes) =>
    minutes >= 60
      ? minutes % 60 === 0
        ? `omtrent hver ${minutes / 60}. time`
        : `omtrent hver ${Math.floor(minutes / 60)},5 time`
      : `omtrent hvert ${minutes}. minutt`,
  linesPerDay: (count, atLeast) =>
    `${atLeast ? "minst " : ""}${count} ${count === 1 ? "avgang" : "avganger"} i døgnet`,
  linesMore: (count) => `Vis ${count} flere linjer`,
  linesFewer: "Vis færre",
  linesRealtime: "Sanntidsdata",
  linesCredit: "Ruteplan fra Entur.",
  camerasTitle: "Veikamera nå",
  camerasTowards: "mot",
  camerasRefresh: "Oppdater",
  camerasRefreshHint: "Hent ferske bilder fra kameraene.",
  camerasCredit: "Bilder fra Statens vegvesen (NLOD).",

  notifyLabel: "Varsle om værskifte",
  notifyHint:
    "Appen sjekker varselet på nytt mens den er åpen, og sier fra når et utsiktspunkt klarner eller tåkner — eller når nordlyset står klart. Varslene kommer fra denne enheten, så en helt lukket nettleser blir ikke vekket.",
  notifyOn: "Varsling er på",
  notifyOff: "Slå på varsling",
  notifyBlocked: "Varsling er avslått i nettleseren. Slå den på i innstillingene for siden.",
  notifyUnsupported: "Denne nettleseren støtter ikke varsler.",
  notifyViewImprovedTitle: (stop) => `${stop} har klarnet`,
  notifyViewImprovedBody: (day, before, after) =>
    `Utsiktsindeksen på dag ${day} er opp fra ${before} til ${after}. Vurder å legge dagen dit.`,
  notifyViewLostTitle: (stop) => `${stop} har tåknet`,
  notifyViewLostBody: (day, before, after) =>
    `Utsiktsindeksen på dag ${day} er ned fra ${before} til ${after}. Se om en annen dag passer bedre.`,
  notifyAuroraTitle: (stop) => `Nordlys over ${stop} i natt`,
  notifyAuroraBody: (chance) =>
    `${chance} % sjanse: mørkt, klart og nok aktivitet samtidig. Det er sjelden alle tre stemmer.`,
  protectedTitle: "Vernet natur her",
  protectedKind: {
    Nasjonalpark: "nasjonalpark",
    Naturreservat: "naturreservat",
    Landskapsvernomraade: "landskapsvernområde",
    Biotopvernomraade: "biotopvernområde",
    BiotopvernVilt: "biotopvern",
    Plantefredningsomraade: "plantefredningsområde",
  },
  protectedRegulation: "Verneforskriften",
  protectedFactSheet: "Faktaark",
  protectedCredit:
    "Verneområder fra Miljødirektoratet (NLOD). Forskriften for området er det som gjelder — den er ulik fra vern til vern.",
  accessFire:
    "Bålforbud i eller nær skog 15. april–15. september. Utenom det: bruk bålplass, aldri på svaberg eller torvmark.",
  accessTent:
    "Telting er fritt i utmark, minst 150 meter fra hus og hytte, og maks to netter på samme sted. I nasjonalparker kan forskriften si noe annet.",
  accessLeaveNoTrace:
    "Alt du bærer inn, bærer du ut. Hold deg til stien der det er en — vegetasjonen på fjellet bruker tiår på å komme tilbake.",
  staleData: (when) =>
    `Hentet ${when} og lagret for bruk uten dekning. Sjekk på nytt når du har nett — dette er ikke sanntid.`,
  navigateLabel: "Ta dagen med ut",
  openGoogleMaps: "Google Maps",
  openAppleMaps: "Apple Kart",
  exportGpx: "GPX",
  exportGpxDay: "GPX for dagen",

  hazardKind: {
    avalanche: "Snøskredfare",
    flood: "Flomfare",
    landslide: "Jord- og flomskredfare",
  },
  hazardLevel: (level) => `nivå ${level}`,
  hazardSource: "varsom.no",
  hazardCredit: "Naturfarevarsler fra NVE / varsom.no (NLOD).",
  ferryTitle: "Ferjer på ruta",
  ferrySubtitle:
    "Ruteplanen fra Statens vegvesen for strekningene ruta må over. Ferja er ofte den ene tiden resten av dagen må legges rundt.",
  ferryDuration: (minutes) =>
    minutes >= 60
      ? `${Math.floor(minutes / 60)} t ${minutes % 60 ? `${minutes % 60} min` : ""}`.trim()
      : `${minutes} min`,
  ferryCancelled: "avlyst",
  ferryCredit:
    "Ferjedata fra Statens vegvesen (NLOD). Ruteplan med avlysninger, ikke skipets posisjon — live AIS krever konto.",

  showcaseEyebrow: "Dette ser du på veien",
  showcasePlan: "Planlegg denne ruta",
  showcasePrevious: "Forrige sted",
  showcaseNext: "Neste sted",
  showcaseHint: "Trykk på en rute for å se hva den inneholder.",

  filterLabel: "Vis",
  filterAll: "Alle",
  filterNone: "Ingen",
  placeKind: {
    hotel: "Hotell",
    hostel: "Hostell",
    camping: "Camping",
    cabin: "Hytter",
    charging: "Lading",
    fuel: "Bensin",
    food: "Spisesteder",
    ski: "Ski",
  },

  mapPlaces: "Steder",
  mapPlacesHint:
    "Vis hotell, camping, hytter, lading, spisesteder og skianlegg der de faktisk ligger.",
  mapLegendTitle: "På kartet",
  mapLegend: {
    lodging: "hotell og hostell",
    camping: "camping og hytte",
    charging: "lading",
    food: "spisested",
    ski: "skianlegg",
    hike: "fottur fram",
  },
  mapLegendVehicleTitle: "Langs ruta",
  vehicleGlyphName: {
    tog: "med tog",
    buss: "med buss",
    bobil: "med bobil",
    bil: "med bil",
    fot: "til fots",
  },

  skiTitle: "Ski om vinteren",
  skiLifts: (n) => `${n} ${n === 1 ? "heis" : "heiser"}`,
  skiDownhill: "Alpint",
  skiNordic: "Langrenn",
  skiNone: "Ingen skianlegg registrert her.",

  lodgingTitle: "Overnatting",
  lodgingKind: {
    hotel: "Hotell og pensjonat",
    hostel: "Vandrerhjem",
    camping: "Camping",
    cabin: "Hytter og rorbuer",
  },
  dntHut: "DNT",
  noLodging: "Ingen overnatting registrert i OpenStreetMap her.",

  amenitiesTitle: "I nærheten",
  charging: "Lading",
  fuel: "Bensin",
  food: "Spisesteder",
  fastCharge: "hurtig",
  chargePoints: (n) => `${n} punkt`,
  diesel: "diesel",
  foodType: { restaurant: "restaurant", cafe: "kafé", fast_food: "gatekjøkken" },
  noAmenities: "Ingenting registrert i OpenStreetMap her.",

  bestDayTitle: "Beste avreisedag",
  bestDayPickThis: "Bruk denne datoen",
  bestDayCompare: "Alle dagene i varselvinduet",
  daylightHours: (hours) => `${hours} t dagslys`,

  optimizerApplied: "Ruten er lagt etter værvinduet",
  optimizerConsidered: "Værvindu vurdert",
  optimizerGain: (points) => `+${points} i utsiktsindeks`,
  optimizerSwapNote:
    "Bytter gjøres bare mellom stopp som ligger like ved hverandre langs korridoren, slik at reiseruten er den samme — det er bare hva du bruker dagen til som endres.",

  elevation: "Høyde over havet",
  travelFromStart: "Reisetid fra start",
  suggestedTime: "Anbefalt tid her",
  viewDependent: "Utsiktsavhengig",
  reachedBy: "Nås med",
  daylight: "Dagslys",
  coordinates: "Koordinater",
  enturStop: "Entur-holdeplass",
  whenItRains: "Når det regner:",
  seeOnMap: "Se på kart",
  photoBy: "Foto:",

  videoLink: "Se stedet på video",
  videoSource: "YouTube-søk",
  videoCountry: "Norge",

  placeAlt: "kart",
  placeAltSearch: "søk",
  placeAltHint: "Annet oppslag, om nettsida er nede",
  offersTitle: "Verdt et stopp",
  offerKind: {
    sight: "Severdighet",
    viewpoint: "Utsikt",
    hike: "Fottur",
    museum: "Museum",
    food: "Mat",
    activity: "Aktivitet",
  },
  historyTitle: "Om stedet",
  historySource: "Wikipedia",

  share: "Del rute",
  shared: "Kopiert",
  calendar: "Kalender",
  print: "Skriv ut",

  offlineTitle: "Ingen dekning",
  offlineBody:
    "Du ser den sist lagrede planen. Kart og bilder du har sett før er med; nye ruter og oppdatert vær krever nett.",

  emptyTitle: "Ingen rute ennå",
  emptyBody:
    "Velg innstillingene over og trykk «Lag ruteforslag». Legger du inn en avreisedato, henter vi værvarsel fra MET og faktiske avganger fra Entur — og kan legge dagene etter værvinduet.",
  apiUnreachable: "Fikk ikke kontakt med API-et. Kjører backend på port 5175?",
  genericError: "Klarte ikke å lage ruten.",
  clipboardBlocked: "Nettleseren tillot ikke kopiering. Kopier adressen fra adresselinja i stedet.",

  footerSources:
    "Værdata fra MET Norway Locationforecast, rutedata fra Entur Journey Planner. Steds-, lade- og spisestedsdata fra OpenStreetMap. Bilder fra Wikimedia Commons.",
  footerDisclaimer:
    "Utsiktsindeksen er et estimat basert på værsymbol, nedbør, dagslys og hvor utsiktsavhengig hvert sted er — den erstatter ikke å sjekke varselet før du drar.",
  languageLabel: "Språk",
  contentInEnglish: "Stedsbeskrivelsene er på engelsk.",
  themeLabel: "Tema",
  themeName: { light: "Lyst", dark: "Mørkt", system: "Følg systemet" },

  aiTitle: "Beskriv turen med egne ord",
  aiIntro:
    "Skriv noen setninger, så fyller vi ut skjemaet. Du ser hva vi forsto før planen lages.",
  aiPlaceholder: "Sju dager langs fjordene i september, bobil, rolig tempo",
  aiCta: "Fyll ut skjemaet",
  aiBusy: "Tolker …",
  aiUnderstood: "Slik forsto vi det",
  aiAssumed: "Dette sto ikke i teksten, så vi antok",
  aiUnavailable:
    "Kan ikke tolke fritekst akkurat nå. Skjemaet under gir den samme planen.",
  aiFieldName: {
    corridorId: "rute",
    days: "antall dager",
    mode: "reisemåte",
    vibe: "type tur",
    pace: "tempo",
  },
  aiExamplesLabel: "For eksempel",
  aiExamples: [
    "Ti dager fra Oslo til Bergen med tog, rolig tempo, mest fjord.",
    "Sju dager i fjellet med bobil, aktivt tempo og mye vandring.",
    "Fem dager langs Lofoten med bil i juni, avslappet tempo.",
    "Ni dager Oslo til Tromsø med bil fra 14. september, miks av fjell og by.",
  ],
  aiOrPickInterests: "eller velg det du er ute etter",

  monthShort: ["jan", "feb", "mar", "apr", "mai", "jun", "jul", "aug", "sep", "okt", "nov", "des"],

  addStopHint: "Hold pekeren over et sted for bilde og beskrivelse, eller trykk «i» for detaljene.",
  addStopDwell: (hours) => `${hours} t på stedet`,
  addStopHike: (hours) => `fottur ${hours} t`,
  addStopAbout: (name) => `Om ${name}`,

  winterTitle: "Vinteren her",
  winterStatus: {
    seasonal: "vinterstengt",
    harder: "krevende om vinteren",
    expert: "for erfarne om vinteren",
  },
  winterNotInSeason: "utenfor sesong",
  winterSeason: (months) => `Normalt åpent: ${months}.`,
  winterDoing: (month) => `Dette gjør man her i ${month}`,
  winterKind: {
    aurora: "nordlys",
    "ski-alpine": "alpint",
    "ski-nordic": "langrenn",
    "ski-touring": "topptur",
    dogsled: "hundekjøring",
    reindeer: "rein og samisk kultur",
    snowshoe: "truger",
    whale: "hvalsafari",
    fishing: "fiske",
    sauna: "badstu og isbad",
    sledding: "aking",
    market: "marked",
    museum: "museum",
    "ice-climbing": "isklatring",
    "fjord-cruise": "fjordcruise",
    snowmobile: "snøscooter",
    "guided-hike": "ført tur",
    skating: "skøyter",
    "cable-car": "taubane",
    glacier: "bre",
  },
  winterFullDay: "hele dagen",
  winterMonths: (months) => `Sesong: ${months}`,
  winterBookAhead: (days) =>
    days === 0
      ? "kan bestilles samme dag"
      : `bestill ${days} ${days === 1 ? "dag" : "dager"} i forveien`,

  outlookTitle: "Hva måneden vanligvis gir",
  outlookEstimate: "anslag, ikke varsel",
  outlookScore: "anslått utsiktsindeks",
  outlookRange: "normalt langs ruta",
  outlookColdestAt: (place) => `kaldest på ${place}`,
  outlookWetDays: "døgn med nedbør, av 10",
  outlookAsSnow: "av nedbøren som snø",
  outlookSource: (covered, total, horizon, extendedHorizon) =>
    `Klimanormaler 1991–2020 fra ERA5, høydekorrigert, for ${covered} av ${total} stopp langs ruta. MET varsler ${horizon} døgn fram og ECMWF ${extendedHorizon}; lenger enn det finnes ikke et dagsvarsel med treffsikkerhet hos noen — atmosfæren er kaotisk, og da er klima det ærligste svaret.`,

  extendedTitle: "Langtidsvarselet for dagene dine",
  /*
   * «Langtidsvarsel» og ikke «anslag». Dette *er* et varsel for datoene — det
   * varierer fra dag til dag — og å kalle det et anslag ville gjort det til det
   * samme som klimanormalen, som er det ene det ikke er.
   */
  extendedBadge: "varsel, lavere sikkerhet",
  extendedScore: "utsiktsindeks, varslet",
  extendedReach: "døgn modellen rekker",
  extendedReachNote: (nearHorizon) => `det finmaskede rekker ${nearHorizon}`,
  extendedGrid: "km per rutecelle",
  extendedGridNote: (farthestKm) => `ditt fjerneste stopp: ${farthestKm} km`,
  extendedDays: "av rutas dager dekket",
  extendedDaysAll: "hele ruta er varslet",
  extendedDaysShort: (uncovered) => `${uncovered} ligger forbi modellen`,
  extendedDialLabel: "langtidsvarsel",
  extendedSpan: (daysOut, nearHorizon, horizon) =>
    `Avreisen din ligger ${daysOut} døgn fram. Døgn 1–${nearHorizon} har det finmaskede varselet, døgn ${nearHorizon + 1}–${horizon} har dette.`,
  extendedSource: (model, refinesOn) =>
    `${model} via Open-Meteo. Temperaturene er høydekorrigert mot hvert stopps egen høyde. Det finmaskede varselet fra MET overtar den ${refinesOn}.`,

  servicesTitle: "Billetter og praktisk",
  servicesTicketsNote:
    "Selger billetter til all norsk kollektivtrafikk — tog, buss, båt og ferje. Samme kilde avgangene i planen kommer fra.",
  servicesTollsNote:
    "Bompengeordningen. En brikke gir omtrent 20 % rabatt, og den regnes alt inn i kostnadsanslaget.",
  servicesRoad: "Veimeldinger",
  servicesRoadNote:
    "Stengte veier og kolonnekjøring, oppdatert gjennom døgnet. Les den samme morgenen du skal over fjellet.",
  servicesRental: (place, noun) => `${noun} i ${place}`,
  rentalNoun: { bil: "Leiebil", bobil: "Bobilutleie", motorsykkel: "MC-utleie" },
  servicesRentalNote:
    "Et søk, ikke en anbefaling: appen vet ingenting om pris eller tilgjengelighet, og navngir derfor ingen kjede.",
  servicesFoot:
    "Lenkene går til de offisielle tjenestene og er prøvd. Leiebil er et søk — appen tjener ingenting på noen av dem.",

  connectionFrom: (station) => `fra ${station}`,
  connectionLater: "Senere:",

  planSideLabel: "Oppslag om turen",
  foldWeather: "Vær og timing",
  foldCost: "Kostnad og pakking",
  foldShare: "Del og hør",

  splitTitle: "Del reisekostnadene",
  splitLead: (range) =>
    `Drivstoff, bom og ferje er ${range} for bilen — uansett hvor mange som sitter i den. Det er den delen som blir billigere av selskap.`,
  splitSeats: (seats) => `${seats} i bilen`,
  splitAlone: "alene",
  splitYours: "din gruppe",
  splitNote:
    "Seng, mat og inngangspenger står per person og blir ikke billigere av at bilen er full. Delingen gjelder bare det bilen koster.",

  shareTitle: "Del turen",
  shareNameLabel: "Navnet ditt",
  shareLinkLabel: "Lenke til turen",
  shareNamePlaceholder: "Fornavn",
  sharedEdit: "Endre denne turen",
  shareStart: "Del turen",
  shareCopy: "Kopier lenke",
  shareCopied: "Lenken er kopiert",
  shareCopyFailed: "Kunne ikke kopiere — merk lenken og kopier den selv",
  shareHint:
    "Lenken inneholder hele turen: ruta, dagene, datoen og stoppene du har lagt til eller fjernet. Den som åpner den ser det samme som deg, og kan endre sin egen kopi.",
  tripOwnedBy: (name) => (/[sxz]$/i.test(name) ? `${name}' tur` : `${name}s tur`),
  sharedByLead: (name) => `Du ser ${/[sxz]$/i.test(name) ? `${name}'` : `${name}s`} tur.`,
  sharedByNote:
    "Endrer du noe, endrer du din egen kopi — ikke deres. Del din nye lenke tilbake hvis du vil vise hva du forandret.",
};

const en: Dictionary = {
  eyebrow: "Trip planner",
  title: "Plan a Norway trip that actually works",
  lede: "Weather, mountain passes, ferries, daylight, hikes and transport — combined into one itinerary that plans around the conditions, not just the distance.",
  scrollCue: "Scroll",
  aboutTitle: "About this site",
  contactOff: "The message form is not set up yet. Do get in touch via the link above.",
  sectionFailed: (part) => `${part} could not be shown.`,
  sectionFailedRest: "The rest of your trip is fine.",
  sectionFailedRetry: "Try this part again",
  partMap: "The map",
  partPlan: "The itinerary",
  partPanels: "This section",
  trustLive:
    "Weather from MET, departures from Entur, road reports from the Norwegian Public Roads Administration, avalanche warnings from NVE.",
  trustStops: "118 places along ten routes, with elevation, opening hours and how long you stay.",
  trustNoTracking: "No tracking, no ads, no third-party scripts.",
  trustHonest:
    "Forecasts beyond nine days are marked \u201can estimate, not a forecast\u201d — including when the weather looks good.",
  trustFree: "Free, and no account. The plan lives in the link, not with us.",
  feedbackAsk: "Did the plan turn into anything?",
  feedbackAskBody:
    "One sentence about how it went helps whoever comes next — especially what was missing.",
  feedbackPlaceholder: "What surprised you, or what did not hold up ...",
  feedbackNoThanks: "Not now",
  feedbackClose: "Close the feedback bar",
  aboutLede: (author) =>
    `Built by ${author}. A trip planner that plans around the conditions — the weather, the mountain passes, the ferries and the daylight — rather than only around the sights.`,
  aboutBody:
    "Got a suggestion, spotted something wrong, or missing a place that should be here? Send a message. The weather, departures and road reports come from MET, Entur, the roads authority and NVE — but which places are included is an editorial call, and it gets better for being argued with.",
  contactMessageLabel: "Message",
  contactPlaceholder: "Tell me what you think ...",
  contactNameLabel: "Name (optional)",
  contactReplyLabel: "Email (optional)",
  contactReplyHint: "Only if you want a reply. A suggestion does not need one.",
  contactSend: "Send message",
  contactSending: "Sending ...",
  contactSent: "Thank you — your message has been sent.",
  contactFailed: "The message did not get through. Try again shortly.",
  contactTooShort: (min) => `Write at least ${min} characters, so there is something to answer.`,

  rationaleTitle: "Why this day?",
  rationaleUnchanged:
    "This day stands as the geographically even split left it — the forecast gave no reason to move the boundaries here.",
  rationaleClearAndViewHeavy:
    "This is the clearest day in the forecast, so the most view-dependent stops are concentrated here. The route itself has not changed.",
  rationaleClear:
    "This is the clearest day in the forecast, and the day boundaries follow from that. The route itself has not changed.",
  rationaleMurky:
    "This is the greyest day in the forecast, so it is given over to the stops that hold up in bad weather. The route itself has not changed.",
  rationaleRearranged:
    "The day boundaries were moved to follow the forecast, so the most weather-sensitive parts of the route fall on the clearest days. The route itself has not changed.",

  whyTitle: "Why normal itineraries break",
  whyLede:
    "What ruins a Norwegian road trip is rarely in the guidebook. This is what the planner handles for you.",
  whyCards: [
    {
      title: "Weather takes the view",
      body: "Viewpoints move to the clearest days, and fog is fetched as its own figure from MET — not just a weather icon.",
    },
    {
      title: "The hike takes the day",
      body: "A twelve-hour hike gets the day to itself. The other stops move, and we say so when the daylight will not stretch.",
    },
    {
      title: "The ferry will not wait",
      body: "Sailings are fetched for the day you actually cross, and sit on that day in the plan — not in a list at the bottom.",
    },
    {
      title: "The mountain pass may be shut",
      body: "Closures and convoy driving come from the roads authority and are tied to the stretch you drive that day.",
    },
    {
      title: "The mountains cost energy",
      body: "Every metre of climb is counted into fuel and electricity, alongside the toll rates along your route.",
    },
    {
      title: "The signal disappears",
      body: "The plan is saved to your phone with maps, emergency numbers and coordinates, so it works when the network does not.",
    },
  ],

  corridorLabel: "Which journey?",
  corridorHint: "Seven routes, from the Bergen Line to the midnight sun",
  longJourneys: "Long journeys",
  longJourneysHint: "Several routes joined end to end — a week or more through the country.",
  helpMeChoose: "Not sure where to go?",
  helpMeChooseHint: "Tell us what you want to experience, and we will suggest a route.",
  interestsLabel: "What are you after?",
  interestName: {
    fjord: "Fjords",
    fjell: "Mountains and hiking",
    by: "Cities and culture",
    historie: "History",
    mat: "Food and drink",
    nordlys: "Northern lights",
    midnattssol: "Midnight sun",
    strand: "Beaches",
    dyreliv: "Wildlife",
    tog: "The train journey itself",
    ski: "Skiing",
  },
  monthLabel: "When are you travelling?",
  monthHint: "The month decides what the sky can offer, not just the weather",
  monthAnswerTitle: "When should you come?",
  monthAnswerHint: "Say what you want to see, and we name the months that deliver it. There is no best month — only months that give you different things.",
  monthAnswerLead: "Every month gives something and takes something. Pick what you are after.",
  monthAnswerNone: "No month gives you everything you picked. The northern lights and the midnight sun rule each other out — you have to choose.",
  monthAnswerMatches: (months) => `Come in ${months}.`,
  monthNoSeason: (names) => `The month does not decide ${names}. Fonna and Galdhøpiggen are summer ski centres, and the whale safari off Andenes runs in winter.`,
  monthGiveMidnightSun: "Midnight sun north of the Arctic Circle",
  monthGiveAurora: (stops) => `Northern lights — ${stops} places offer them`,
  monthGiveAllOpen: "All 118 stops are open",
  monthGiveQuiet: (load) => `Quiet — ${load} % of the route at its busiest`,
  monthGiveLongDays: (hours) => `Long days — ${hours} hours of light in Tromsø`,
  monthTakeClosures: (count, names) => `${count} stops closed, among them ${names}`,
  monthTakeShortDays: (hours) => `Short days — ${hours} hours of light in Tromsø`,
  monthTakePolarNight: "Polar night in the north — the sun does not rise",
  monthTakeBusy: (load) => `Busy — ${load} % of the route at its busiest`,
  monthTakeNoAurora: "No northern lights — the nights do not get dark enough",
  monthTakeNoMidnightSun: "No midnight sun",
  monthCaveatRain: "Rain will not decide this: the share of wet days sits between 44 and 58 % in every single month. In Oslo and Geiranger, July and August are the wettest.",
  monthCaveatWhere: "Where you are matters more than when. The spread between places in one month is wider than the spread between months.",
  monthCaveatNormals: "Temperature and rainfall are 1991—2020 normals. They say what a month is usually like, not what a given week will be.",
  interestOffSeason: (month) => `not in ${month}`,
  interestOffSeasonWhy: (interest, month) =>
    `${interest} does not happen anywhere in Norway in ${month} — that is astronomy, not weather. Pick another month, or something else to look for.`,
  interestOffSeasonChosen: (names, month) =>
    `${names} is not possible in ${month}, and is not counted in the suggestion.`,
  interestOffSeasonRemove: "Remove",
  crowdTitle: "When it is busy",
  crowdHint: "Guest arrivals per month along the route, as a share of the route's own busiest month. The figures are Statistics Norway's, averaged over seven full years.",
  crowdSummary: (peak, quiet, swing) => `${peak} is busiest. ${quiet} is quietest — ${swing} times fewer guests.`,
  crowdFlat: (peak) => `${peak} is busiest, but the route has guests all year.`,
  crowdBusiest: (region, swing) => `Widest swing: ${region}, ${swing} times between peak and trough.`,
  crowdRegions: (names) => `Measured across ${names}.`,
  crowdCaveat: "The figures count people who stay overnight. Day trippers and cruise passengers are not included — and they are exactly who fills the quay in Geiranger.",
  crowdPeakLabel: "busiest",
  crowdQuietLabel: "quietest",
  crowdBar: (month, load) => `${month}: ${load} % of the route at its busiest`,
  browseTitle: "Browse",
  browseTrips: "All routes",
  browsePlaces: "All places",
  placeTripTitle: "Only after one place?",
  placeTripHint: "Not everyone wants the whole road trip. Pick the place you saw, and we will tell you how long it takes, how to get there, and what is within a day trip.",
  placeTripLead: "Pick a place and we will work out the rest.",
  placeTripPick: "Where do you want to go?",
  placeTripFrom: "Where do you arrive?",
  placeTripAnywhere: "Not sure yet",
  placeTripDaysLabel: "How many days?",
  placeTripNeed: (days) => `The place needs at least ${days} days.`,
  placeTripGetting: (from, hours, km) => `From ${from}: about ${hours} h, ${km} km as the crow flies.`,
  placeTripNearbyTitle: "Within a day trip",
  placeTripNearby: (hours) => `${hours} h away`,
  placeTripNothingNear: "The place stands on its own — there is nothing else within a day trip.",
  placeTripDay: (n) => `Day ${n}`,
  placeTripOpenRoute: "See the whole route this place sits on →",
  placeTripPlan: "Build the full plan for this place →",
  focusOnly: (place) => `Only ${place}`,
  focusWhole: "Back to the whole route",
  focusNote: (place) =>
    `This plan is about ${place} and what sits within a day trip of it — not about the whole route.`,
  exploreTitle: "Explore places",
  exploreHint: "Not sure where to go? Browse every place. Tap a photo to see what it is, what it is known for, and which route it sits on.",
  exploreSearch: "Search for a place",
  exploreAllRoutes: "All routes",
  exploreCount: (n) => `${n} places`,
  exploreNoMatch: "Nothing matches that search. Try another word, or pick all routes.",
  exploreFamousFor: "Known for",
  exploreBackground: "Background",
  exploreOnRoute: "Sits on",
  exploreWatch: "Watch video",
  exploreOpenPage: "Its own page",
  exploreClose: "Close",
  explorePlanHere: "Plan a trip through here",
  exploreHikeDay: (hours) => `A walk — allow ${hours} hours.`,
  vehicleOffSeason: (month) => `Rare in ${month}`,
  vehicleOffSeasonWhy: (vehicle, month) =>
    `${vehicle} is little used in ${month}: the mountain passes the route crosses are shut, and winter tyres for one barely exist. The choice stands, but the plan assumes summer roads.`,
  monthName: [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ],
  findRoutes: "Find my route →",
  finding: "Weighing the routes…",
  recommendTitle: "This fits best",
  recommendPick: "Choose this",
  recommendMatch: (score) => `${score}% match`,
  recommendEmpty: "Pick at least one thing you want to experience.",
  recommendClose: "Close suggestions",
  stopCount: (n) => `${n} places`,
  endToEnd: (hours) => `${hours} total`,

  previewRoute: "Preview",
  chosenRoute: "Chosen route",
  changeRoute: "Change route",
  tripLabel: "How do you want to travel?",
  tripHint: "Length, mode and pace — all of it can be changed afterwards",
  daysLabel: "Number of days",
  fromLabel: "Where are you travelling from?",
  fromNone: "I start where the route starts",
  fromHint: "We add the leg to get there, with departures where they exist",
  returnLabel: "And home again?",
  returnNone: "I am not saying anything about the way back",
  returnHint:
    "If you have to get back somewhere, the journey takes time out of the trip. If it costs a day, the plan says which one.",
  homewardTab: "Home",
  homewardTitle: (place) => `Home to ${place}`,
  approachHeading: (place) => `Getting to ${place}`,
  daysOption: (n) => `${n} days`,
  daysFewer: "Fewer days",
  daysMore: "More days",
  daysRoomy: (days) =>
    `${days} days is roomier than this route needs. The days come out short, with plenty of time in each place — a good choice if you want calm, and too much if you want to cover ground.`,
  daysTight: (days) =>
    `${days} days on this route is mostly travelling. It can be done, but expect long stretches and few stops along the way.`,
  modeLabel: "How are you travelling?",
  modeName: { tog: "Train", bil: "Car", buss: "Bus", fottur: "On foot" },
  travelModeName: {
    kollektiv: "Public transport",
    tog: "Train only",
    buss: "Bus only",
    bil: "Car",
  },
  modeUnavailableHint: (corridor) => `This mode does not run the whole way on ${corridor}.`,
  modeWidenedHint: (mode, corridor) =>
    `${mode} does not run the whole way on ${corridor}. The route will be planned by public transport — the train where it runs, bus or boat onward.`,
  vibeLabel: "What are you here for?",
  vibeName: { fjell: "Mountains", fjord: "Fjords", by: "Cities", miks: "A bit of everything" },
  paceLabel: "Pace",
  paceName: { avslappet: "Relaxed", aktiv: "See as much as possible" },
  paceDetail: { avslappet: "1 stop/day", aktiv: "2 stops/day" },
  dateLabel: "Departure date",
  dateHint: "Optional — unlocks weather, daylight, the aurora forecast and real departures",
  clearDate: "Clear date",
  weatherToggleTitle: "Plan around the weather window",
  weatherToggleOn:
    "Fetches the forecast for every stop on every date and moves the day boundaries so the viewpoints land on the clear days.",
  weatherToggleOff: "Pick a departure date first — we need to know which days to fetch the forecast for.",
  bestDayToggleTitle: "Suggest the best day to leave",
  bestDayToggleOn:
    "Scores the whole trip for every possible start date in the forecast window and tells you which gives you the most to see.",
  bestDayToggleOff: "Needs a departure date — we look nine days forward from it.",
  generate: "Build the route →",
  generating: "Fetching weather and departures…",

  statDistance: "As the crow flies",
  statDays: "Days",
  onwardTitle: (place) => `On to ${place}`,
  dayInfoTitle: "Good to know",
  stopExtrasSummary: "Things to do, places to stay, history and cameras",
  dayInfoSummary: (count) => `${count} things about this day`,
  stopMore: "More about this place →",
  summaryFoldSummary: (days, stops) => `${days} days · ${stops} stops · as an image`,
  onwardToDay: (day) => `Day ${day} →`,
  statTravel: "Travel time",
  statHighest: "Highest point",
  statStops: "Stops",
  unitKm: "km",
  unitMasl: "m",
  seaTitle: "Sea and swimming",
  seaHere: "Measured where you stand",
  seaAt: (km) => `Measured ${km} km away`,
  seaWaves: (metres) => `waves ${metres} m`,
  seaSource: "MET ocean forecast, updated today",
  soundtrackTitle: "Something to listen to",
  soundtrackSubtitle: (hours) =>
    `${hours} hours of travel. Music and podcasts with a real connection to the route.`,
  soundtrackPodcast: "podcast",
  soundtrackSearchNote:
    "The links search for the artist — the app creates no playlist and asks for no access to your account.",

  bathingKind: { beach: "beach", swim: "swimming spot", resort: "lido" },

  heritageTitle: "Listed buildings",
  heritageDated: (dated) => `dated ${dated}`,
  heritageMore: "Open in Kulturminnesøk",
  heritageSource: "Directorate for Cultural Heritage, Askeladden",

  climateTitle: "Normal for this month",
  climateRange: (min, max) => `${min} to ${max} °C`,
  climateWetDays: "days with rain",
  climateClearNights: "clear nights",
  climateSnowDays: "days with snow",
  climateFrostNights: "nights with frost",
  climateSource: "1991–2020 normals, ERA5. Not a forecast — a thirty-year average.",

  packingTitle: "What to bring",
  packingWetNote: (percent) => `It rains on roughly ${percent}% of days this month.`,
  packingSnowNote: "Some of the stops normally have snow.",
  packingClothing: "Clothes",
  packingGear: "Other",
  packingShop: "Search for where to buy this",
  packingShopFor: (item) => `Search for where to buy ${item}`,
  packingShopHint:
    "The buttons are searches, not a recommendation — the app has not checked any shop and earns nothing from where you buy.",

  summaryRunningTitle: "Driving the route",
  summaryFuelUnit: "litres of petrol",
  summaryElectricUnit: "kWh",
  summaryTollUnit: "NOK in tolls",
  summaryClimbNote: (ascentM, percent) =>
    `${ascentM} m of climbing is included, and it accounts for ${percent}% of the energy.`,
  summaryTollNote: (schemes) =>
    schemes === 1
      ? "One toll scheme along the route, at the rate published in NVDB."
      : `${schemes} toll schemes along the route, at the rates published in NVDB. Which ones you pass depends on the road you take.`,
  energySummary: (litres, kWh, ascentM) =>
    `Around ${litres} litres of petrol, or ${kWh} kWh — including ${ascentM} m of climbing, which costs the same however gently you drive.`,
  tollCertainTitle: "You pay these either way",
  tollPossibleTitle: "Depends on the road you take",
  tollDiscountNote: (percent) =>
    `Rates are the "small car" tariff from NVDB, with the ${percent}% tag discount applied. Electric cars pay less in many schemes.`,


  mapTitle: "The route along the corridor",
  mapSubtitle: "Drawn from each stop's real coordinates. Hover a point for a photo and details.",
  map3dSubtitle:
    "Norway's national topographic map draped over real elevation data. Drag to spin, hold Ctrl while dragging to tilt the camera.",
  mapView3d: "3D terrain",
  mapViewFlat: "Schematic",
  mapLoading: "Loading terrain…",
  flyStart: "Fly the route",
  flyStop: "Stop",
  basemapTopo: "Topographic",
  basemapGrey: "Grey tone",
  terrainTitle: "Terrain profile",
  terrainSubtitle: "The elevation along the route, seen from the side. Click a peak for details.",
  legendNotChosen: "not selected",
  tagName: { fjell: "mountain", fjord: "fjord", by: "city" },

  dayTab: (n) => `DAY ${n}`,
  travelSuffix: "travel",
  departuresHeading: "Departures from Entur",
  connectionSummary: (km, duration, mode) => `${km} km as the crow flies · about ${duration} by ${mode}`,
  connectionWalkable: "Walking distance",
  connectionTrailhead: "Final stretch on foot",
  connectionAlsoBy: (mode) => `There is also a ${mode} on this stretch`,
  connectionDirections: "Directions",
  journeyAlternatives: "Other ways",
  journeyTransfers: (n) => (n === 1 ? "1 transfer" : `${n} transfers`),
  journeyDirect: "no transfers",
  journeyFastest: "fastest",

  constraintFullDay: "All day",
  constraintHikeOwnsDay:
    "The hike takes the whole day, so we moved the other stops to the neighbouring days.",
  constraintDaylightFits: (hours) => `${hours} hours of daylight — the hike fits in the light`,
  constraintDaylightShort: (hours) => `Only ${hours} hours of daylight — the last stretch is in the dark`,
  constraintFerry: "Ferry",
  constraintFerryMissed: (time) => `Miss the ${time} and the rest of the day rides on the next sailing.`,
  constraintFerryNone: "No sailings left that day — the crossing needs replanning.",
  constraintOnTodaysLeg: (from, to) => `On the ${from}–${to} stretch, which you drive today.`,

  alertClimateTitle: "Dress for the mountain",
  alertClimateBody: (ascentM, topM, dropC) =>
    `This leg climbs ${ascentM} metres, up to ${topM} m above sea level. Expect it to be about ${dropC} °C colder up there than where you are now.`,
  alertTunnelTitle: (name, km) => `${name} — ${km} km tunnel`,
  alertTunnelBody: (road, minutes) => `On ${road}, roughly ${minutes} minutes under the mountain.`,

  sunTitle: "Sun and daylight",
  sunArrival: (time) => `Arriving around ${time}`,
  sunRise: "Sunrise",
  sunSet: "Sunset",
  sunGolden: "Best light for photos",
  sunDaylight: "Daylight when you arrive",
  sunAfterDark: "You arrive after sunset — bring a head torch",
  sunMidnight: "Midnight sun — light around the clock",
  sunPolarNight: "Polar night — the sun does not rise",

  clarityLabel: "Visibility",
  clarityStatus: {
    klar: "Clear view",
    delvis: "Partly foggy or cloudy",
    tett: "Thick fog — limited view",
  },
  clarityDetail: (fog, cloud) => `${fog}% fog, ${cloud}% cloud cover`,

  vehicleLabel: "What are you driving?",
  vehicleHint: "Only changes what the plan warns you about — not which stops you get",
  vehicleName: {
    bil: "Ordinary car",
    bobil: "Motorhome or caravan",
    motorsykkel: "Motorcycle",
  },
  vehicleWarning: "Demanding for large vehicles",
  vehicleNarrow: "Single lane with passing places",
  vehicleSteep: "Steep climb",
  vehicleMaxLength: (metres) => `Length limit ${metres} m`,

  producerBadge: "Local find",
  producerTitle: "Local food and mountain farms",
  producerKind: {
    sider: "Cidery",
    frukt: "Fruit stall",
    seter: "Mountain farm",
    gardsutsalg: "Farm shop",
  },
  producerSelfService: "Self-service — bring cash or Vipps",
  producerFind: "find on the map",
  fromStart: (place) => `from ${place}`,
  freeDay: "Free day — nothing scheduled. Explore at your own pace.",
  viewIndex: "View index",
  viewIndexShort: "view",
  weatherAgainstView: "The weather is against the view here.",

  addStop: "Add a stop",
  addStopNearby: (n) => `${n} stops along this stretch`,
  addStopAll: (n) => `All ${n} available stops`,
  showAll: (n) => `Show all ${n} available stops`,
  removeStop: (name) => `Remove ${name} from the route`,
  resetAdjustments: (n) => `Reset ${n} ${n === 1 ? "change" : "changes"}`,
  close: "Close",

  budgetTitle: "What will it cost?",
  budgetPerPerson: (n) => `For ${n} people`,
  budgetRange: (low, high) => `${low}–${high}`,
  budgetTotal: "Altogether",
  budgetCurrency: "Currency",
  budgetRateNote: (date) => `Rate from Norges Bank, ${date}.`,
  budgetTravellers: "Travellers",

  auroraTitle: "Aurora tonight",
  auroraChance: (percent) => `${percent}% chance`,
  auroraKp: (kp, needed) => `Kp ${kp} — Kp ${needed} needed here`,
  auroraLimited: {
    daylight: "Darkness is the limit — the nights are not long enough here yet.",
    clouds: "Cloud is the limit. Clearer skies further inland may help.",
    activity: "Activity is the limit — Kp needs to rise for the aurora to reach here.",
  },
  auroraGood: "Everything lines up: dark, clear, and activity enough.",
  auroraSource: "NOAA Space Weather Prediction Center",

  hikeTitle: "The last stretch on foot",
  hikeRoundTrip: (hoursMin, hoursMax) =>
    `${hoursMin === hoursMax ? hoursMin : `${hoursMin}–${hoursMax}`} hours round trip`,
  hikeDistance: (km) => `${km} km`,
  hikeAscent: (m) => `${m} m of ascent`,
  hikeGradeName: {
    enkel: "easy",
    moderat: "moderate",
    krevende: "demanding",
    ekspert: "expert",
  },
  hikeTrailhead: "The path starts at",
  hikeSeason: "Season",
  hikeReachedBy: "The trailhead is reached by",
  hikeGuidesTitle: "Guided tours",
  hikeBookAhead: (days) => `book ${days} ${days === 1 ? "day" : "days"} ahead`,
  hikeGuidesNone: "The route is marked and walked without a guide in season.",
  hikeBadge: "On foot",

  trafficTitle: "On the road now",
  trafficSubtitle:
    "Active messages from the Norwegian Public Roads Administration within 30 km of the route. Closures and convoy driving first.",
  trafficCount: (n) => `${n} ${n === 1 ? "message" : "messages"}`,
  trafficBlocking: (n) => `${n} ${n === 1 ? "closure" : "closures"}`,
  trafficSeverity: {
    closed: "closed",
    convoy: "convoy driving",
    warning: "warning",
    roadwork: "roadworks",
  },
  trafficMountainPass: "mountain pass",
  trafficOnRoute: "— the stretch you drive",
  trafficOffRoute: "Nearby, not on your route",
  trafficFromRoad: (km) => `${km} km from the stretch`,
  trafficNear: (km) => `${km} km from the route`,
  trafficUntil: (date) => `until ${date}`,
  trafficSource: "Roads Administration",
  trafficCredit:
    "Traffic messages from the Norwegian Public Roads Administration (NLOD). Refreshed every five minutes.",
  copy: "Copy",
  summaryTitle: "The trip in numbers",
  summarySubtitle: "A card you can download or paste into a conversation.",
  summaryEyebrow: "Norway itinerary",
  summaryClearHours: "h clear sky",
  summaryHikeHours: "h on foot",
  summaryClearNote: (viewpoints) =>
    `An estimate for ${viewpoints} ${viewpoints === 1 ? "viewpoint" : "viewpoints"}: planned time there multiplied by how much clear sky the MET forecast symbol implies. An estimate, not a promise.`,
  summaryNoDate: "Pick a date to get an estimate of how much clear sky the viewpoints will get.",
  summaryBest: (stopName) => `Best conditions: ${stopName}`,
  summaryHighest: (stopName, metres) => `Highest point: ${stopName}, ${metres} m`,
  summaryFooter: "reise-planer · weather from MET Norway",
  summaryDownload: "Download image",
  summaryCopy: "Copy image",
  summarySaved: "Downloaded",
  summaryCopied: "Copied",
  roadbookDays: "days",
  roadbookStops: "stops",
  roadbookNotes: "Your own notes",
  roadbookFooter:
    "Made with Reise-planer. Departures from Entur, weather from MET Norway, road conditions from the Norwegian Public Roads Administration, maps from Kartverket. Times are estimates — check departures and road status before you leave.",
  roadbookPrint: "Print / save as PDF",
  emergencyTitle: "Emergency numbers in Norway",
  emergencySubtitle:
    "Free from any mobile, including without a SIM card and without coverage from your own operator.",
  emergencyWho: {
    medical: "Medical emergency — ambulance, air ambulance",
    police: "Police — also mountain rescue and missing persons",
    fire: "Fire — and acute pollution",
    sea: "Sea rescue — Joint Rescue Coordination Centre, accidents at sea and in fjords",
    doctor: "Out-of-hours doctor — illness and injury that is not life-threatening",
    road: "Road report — the traffic centre, closed roads and convoy driving",
  },
  emergencyWhatTitle: "What to say, in this order",
  emergencySteps: [
    {
      title: "Where you are",
      detail:
        "This is asked first, every time. Read out the coordinates below, or give the road number, the nearest known place and the municipality. Help that does not know where to go is no help.",
    },
    {
      title: "What has happened",
      detail: "Briefly: a fall, a road accident, unconscious, missing. Do not explain more than you are asked.",
    },
    {
      title: "How many, and how they are",
      detail: "Are they breathing? Awake? Bleeding heavily? This decides what gets sent.",
    },
    {
      title: "Who you are, and the number you are calling from",
      detail: "They will call back if the line drops, and in the mountains it often does.",
    },
    {
      title: "Do not hang up",
      detail:
        "The operator guides you while help is on the way, including through CPR. Stay on the line until told otherwise.",
    },
  ],
  emergencyFactsTitle: "Worth knowing on these routes",
  emergencyFacts: [
    "Emergency calls go through any network present where you stand, not only your own operator's. If your phone says «no service», 113 may still connect — try it.",
    "The «Hjelp 113» app from Norwegian Air Ambulance sends your position automatically. Download it before the trip, not during it.",
    "Several stops on these routes have no mobile coverage at all — Hardangervidda, Trolltunga, Kjerag, Romsdalseggen. Agree when you are expected back, with someone who will miss you.",
    "If you are deaf or hard of hearing, text 1412. That is the emergency number for text messages.",
    "In the mountains the police (112) coordinate rescue with the Red Cross, Norwegian People's Aid and air ambulance. Call 112, not the individual groups.",
  ],
  emergencyPositionsTitle: "Positions along the route, ready to read out",
  emergencyPositionsHint:
    "Decimal degrees — the format the emergency services ask for. Say «north» before the first number and «east» before the second.",
  goldenBest: "Best photo light at",
  goldenWindow: (from, to, length) => `${from}–${to} · ${length} of low, warm light`,
  goldenAllDay: (length) =>
    `The sun never climbs above 6°, so the whole ${length} of daylight is golden`,
  goldenMidnightSun: (length) => `Low midnight sun — ${length} of golden light`,
  goldenMorning: (from, to) => `Morning light ${from}–${to}`,
  goldenPolarNight: "Polar night: no sun above the horizon, and not enough light to shoot in.",
  goldenSunTooHigh:
    "High midnight sun: the sun never drops below 6°, so the light stays hard all day.",
  goldenMinutes: (minutes) => `${minutes} min`,
  goldenHours: (hours) => `${hours} h`,
  goldenHoursMinutes: (hours, minutes) => `${hours} h ${minutes} min`,
  linesTitle: "Trains and buses from here",
  linesSubtitle: "What passes through over the next 24 hours, and how often.",
  linesLoading: "Fetching the timetable …",
  linesFailed: "Could not reach Entur just now. Try again shortly.",
  linesNone: "No departures registered here in the next 24 hours.",
  nearbyTitle: (km) => `Stops within ${km} km`,
  nearbyAway: (km) => `${km} km`,
  nearbyNone: (km) => `No stop within ${km} km as the crow flies.`,
  nearbyOffNetwork:
    "No walking route from here — we cannot find a way to a stop from this coordinate.",
  nearbyUnavailable: "Could not find out what is nearby.",
  nearbyNote:
    "Nearest stops as the crow flies. Some have no departures registered right now — a summer-only quay and a retired one look the same in the timetable.",
  linesEvery: (minutes) =>
    minutes >= 60
      ? minutes % 60 === 0
        ? `about every ${minutes / 60} ${minutes === 60 ? "hour" : "hours"}`
        : `about every ${(minutes / 60).toFixed(1)} hours`
      : `about every ${minutes} minutes`,
  linesPerDay: (count, atLeast) =>
    `${atLeast ? "at least " : ""}${count} ${count === 1 ? "departure" : "departures"} a day`,
  linesMore: (count) => `Show ${count} more lines`,
  linesFewer: "Show fewer",
  linesRealtime: "Live data",
  linesCredit: "Timetable from Entur.",
  camerasTitle: "Road camera now",
  camerasTowards: "towards",
  camerasRefresh: "Refresh",
  camerasRefreshHint: "Fetch fresh images from the cameras.",
  camerasCredit: "Images from the Norwegian Public Roads Administration (NLOD).",

  notifyLabel: "Tell me when the weather turns",
  notifyHint:
    "The app rechecks the forecast while it is open and says so when a viewpoint clears or fogs in — or when the aurora lines up. Notifications come from this device, so a fully closed browser will not be woken.",
  notifyOn: "Notifications on",
  notifyOff: "Turn on notifications",
  notifyBlocked: "Notifications are blocked in the browser. Enable them in this site's settings.",
  notifyUnsupported: "This browser does not support notifications.",
  notifyViewImprovedTitle: (stop) => `${stop} has cleared`,
  notifyViewImprovedBody: (day, before, after) =>
    `The view index on day ${day} is up from ${before} to ${after}. Consider moving the day there.`,
  notifyViewLostTitle: (stop) => `${stop} has fogged in`,
  notifyViewLostBody: (day, before, after) =>
    `The view index on day ${day} is down from ${before} to ${after}. See whether another day suits it better.`,
  notifyAuroraTitle: (stop) => `Aurora over ${stop} tonight`,
  notifyAuroraBody: (chance) =>
    `${chance}% chance: dark, clear and active enough at the same time. All three rarely line up.`,
  protectedTitle: "Protected nature here",
  protectedKind: {
    Nasjonalpark: "national park",
    Naturreservat: "nature reserve",
    Landskapsvernomraade: "protected landscape",
    Biotopvernomraade: "habitat protection area",
    BiotopvernVilt: "wildlife habitat protection",
    Plantefredningsomraade: "plant conservation area",
  },
  protectedRegulation: "The regulation",
  protectedFactSheet: "Fact sheet",
  protectedCredit:
    "Protected areas from the Norwegian Environment Agency (NLOD). The regulation for the specific area is what applies — it differs from one to the next.",
  accessFire:
    "Open fires are banned in or near woodland from 15 April to 15 September. Outside that: use a fire ring, never bare rock or peat.",
  accessTent:
    "Wild camping is free on uncultivated land, at least 150 metres from any house or cabin, and no more than two nights in one spot. In national parks the regulation may say otherwise.",
  accessLeaveNoTrace:
    "Everything you carry in, you carry out. Stay on the path where there is one — mountain vegetation takes decades to come back.",
  staleData: (when) =>
    `Fetched ${when} and stored for use without coverage. Check again when you have a signal — this is not live.`,
  navigateLabel: "Take the day with you",
  openGoogleMaps: "Google Maps",
  openAppleMaps: "Apple Maps",
  exportGpx: "GPX",
  exportGpxDay: "GPX for this day",

  hazardKind: {
    avalanche: "Avalanche danger",
    flood: "Flood danger",
    landslide: "Landslide danger",
  },
  hazardLevel: (level) => `level ${level}`,
  hazardSource: "varsom.no",
  hazardCredit: "Natural hazard warnings from NVE / varsom.no (NLOD).",
  ferryTitle: "Ferries on the route",
  ferrySubtitle:
    "The timetable from the Norwegian Public Roads Administration for the crossings this route must make. The ferry is often the one fixed time the rest of the day has to fit around.",
  ferryDuration: (minutes) =>
    minutes >= 60
      ? `${Math.floor(minutes / 60)} h ${minutes % 60 ? `${minutes % 60} min` : ""}`.trim()
      : `${minutes} min`,
  ferryCancelled: "cancelled",
  ferryCredit:
    "Ferry data from the Norwegian Public Roads Administration (NLOD). Timetable with cancellations, not the vessel's position — live AIS requires an account.",

  showcaseEyebrow: "What you see along the way",
  showcasePlan: "Plan this route",
  showcasePrevious: "Previous place",
  showcaseNext: "Next place",
  showcaseHint: "Tap a route to see what is on it.",

  filterLabel: "Show",
  filterAll: "All",
  filterNone: "None",
  placeKind: {
    hotel: "Hotels",
    hostel: "Hostels",
    camping: "Campsites",
    cabin: "Cabins",
    charging: "Charging",
    fuel: "Fuel",
    food: "Places to eat",
    ski: "Skiing",
  },

  mapPlaces: "Places",
  mapPlacesHint:
    "Show hotels, campsites, cabins, charging, places to eat and ski areas where they actually are.",
  mapLegendTitle: "On the map",
  mapLegend: {
    lodging: "hotels and hostels",
    camping: "campsites and cabins",
    charging: "charging",
    food: "places to eat",
    ski: "ski areas",
    hike: "reached on foot",
  },
  mapLegendVehicleTitle: "Along the route",
  vehicleGlyphName: {
    tog: "by train",
    buss: "by bus",
    bobil: "by campervan",
    bil: "by car",
    fot: "on foot",
  },

  skiTitle: "Skiing in winter",
  skiLifts: (n) => `${n} ${n === 1 ? "lift" : "lifts"}`,
  skiDownhill: "Downhill",
  skiNordic: "Cross-country",
  skiNone: "No ski areas registered here.",

  lodgingTitle: "Where to sleep",
  lodgingKind: {
    hotel: "Hotels and guest houses",
    hostel: "Hostels",
    camping: "Camping",
    cabin: "Cabins and rorbuer",
  },
  dntHut: "DNT",
  noLodging: "No accommodation registered in OpenStreetMap here.",

  amenitiesTitle: "Nearby",
  charging: "Charging",
  fuel: "Fuel",
  food: "Places to eat",
  fastCharge: "rapid",
  chargePoints: (n) => `${n} points`,
  diesel: "diesel",
  foodType: { restaurant: "restaurant", cafe: "café", fast_food: "takeaway" },
  noAmenities: "Nothing registered in OpenStreetMap here.",

  bestDayTitle: "Best day to leave",
  bestDayPickThis: "Use this date",
  bestDayCompare: "Every day in the forecast window",
  daylightHours: (hours) => `${hours} h daylight`,

  optimizerApplied: "The route follows the weather window",
  optimizerConsidered: "Weather window considered",
  optimizerGain: (points) => `+${points} view index`,
  optimizerSwapNote:
    "Swaps only happen between stops that sit next to each other along the corridor, so the travel route is unchanged — only what you do with the day differs.",

  elevation: "Elevation",
  travelFromStart: "Travel time from start",
  suggestedTime: "Suggested time here",
  viewDependent: "View-dependent",
  reachedBy: "Reachable by",
  daylight: "Daylight",
  coordinates: "Coordinates",
  enturStop: "Entur stop place",
  whenItRains: "When it rains:",
  seeOnMap: "See on map",
  photoBy: "Photo:",

  videoLink: "See the place on video",
  videoSource: "YouTube search",
  videoCountry: "Norway",

  placeAlt: "map",
  placeAltSearch: "search",
  placeAltHint: "Another way to find it, if the website is down",
  offersTitle: "Worth stopping for",
  offerKind: {
    sight: "Sight",
    viewpoint: "View",
    hike: "Hike",
    museum: "Museum",
    food: "Food",
    activity: "Activity",
  },
  historyTitle: "About the place",
  historySource: "Wikipedia",

  share: "Share route",
  shared: "Copied",
  calendar: "Calendar",
  print: "Print",

  offlineTitle: "No signal",
  offlineBody:
    "You are seeing your last saved plan. Maps and photos you have viewed before are here; new routes and fresh weather need a connection.",

  emptyTitle: "No route yet",
  emptyBody:
    'Pick your settings above and press "Build the route". Add a departure date and we will fetch the forecast from MET and real departures from Entur — and can lay the days out around the weather window.',
  apiUnreachable: "Could not reach the API. Is the backend running on port 5175?",
  genericError: "Could not build the route.",
  clipboardBlocked: "The browser blocked copying. Copy the address from the address bar instead.",

  footerSources:
    "Weather from MET Norway Locationforecast, transit from Entur Journey Planner. Place, charging and dining data from OpenStreetMap. Photos from Wikimedia Commons.",
  footerDisclaimer:
    "The view index is an estimate built from the weather symbol, precipitation, daylight and how view-dependent each place is — it does not replace checking the forecast before you leave.",
  languageLabel: "Language",
  contentInEnglish: "Place descriptions are in English.",
  themeLabel: "Theme",
  themeName: { light: "Light", dark: "Dark", system: "Match system" },

  aiTitle: "Describe the trip in your own words",
  aiIntro:
    "Write a few sentences and we will fill in the form. You see what we understood before the plan is made.",
  aiPlaceholder: "Seven days along the fjords in September, campervan, relaxed pace",
  aiCta: "Fill in the form",
  aiBusy: "Reading …",
  aiUnderstood: "This is what we understood",
  aiAssumed: "The text did not say, so we assumed",
  aiUnavailable:
    "Cannot interpret free text right now. The form below gives you the same plan.",
  aiFieldName: {
    corridorId: "route",
    days: "number of days",
    mode: "travel mode",
    vibe: "kind of trip",
    pace: "pace",
  },
  aiExamplesLabel: "For example",
  aiExamples: [
    "Ten days from Oslo to Bergen by train, relaxed pace, mostly fjords.",
    "Seven days in the mountains with a campervan, active pace and lots of hiking.",
    "Five days along Lofoten by car in June, relaxed pace.",
    "Nine days Oslo to Tromsø by car from September 14, a mix of mountains and city.",
  ],
  aiOrPickInterests: "or pick what you are looking for",

  monthShort: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],

  addStopHint:
    "Hover a place for a photo and description, or press the info button for the details.",
  addStopDwell: (hours) => `${hours} h on the ground`,
  addStopHike: (hours) => `${hours} h hike`,
  addStopAbout: (name) => `About ${name}`,

  winterTitle: "Winter here",
  winterStatus: {
    seasonal: "closed for the winter",
    harder: "harder in winter",
    expert: "for the experienced in winter",
  },
  winterNotInSeason: "out of season",
  winterSeason: (months) => `Normally open: ${months}.`,
  winterDoing: (month) => `What people do here in ${month}`,
  winterKind: {
    aurora: "northern lights",
    "ski-alpine": "downhill skiing",
    "ski-nordic": "cross-country skiing",
    "ski-touring": "ski touring",
    dogsled: "dog sledding",
    reindeer: "reindeer and Sami culture",
    snowshoe: "snowshoeing",
    whale: "whale safari",
    fishing: "fishing",
    sauna: "sauna and cold water",
    sledding: "sledging",
    market: "market",
    museum: "museum",
    "ice-climbing": "ice climbing",
    "fjord-cruise": "fjord cruise",
    snowmobile: "snowmobile",
    "guided-hike": "guided walk",
    skating: "skating",
    "cable-car": "cable car",
    glacier: "glacier",
  },
  winterFullDay: "takes the day",
  winterMonths: (months) => `Season: ${months}`,
  winterBookAhead: (days) =>
    days === 0
      ? "can be booked the same day"
      : `book ${days} ${days === 1 ? "day" : "days"} ahead`,

  outlookTitle: "What the month usually gives",
  outlookEstimate: "estimate, not a forecast",
  outlookScore: "estimated view index",
  outlookRange: "normal along the route",
  outlookColdestAt: (place) => `coldest at ${place}`,
  outlookWetDays: "days in 10 with precipitation",
  outlookAsSnow: "of the precipitation as snow",
  outlookSource: (covered, total, horizon, extendedHorizon) =>
    `1991–2020 climate normals from ERA5, corrected for elevation, for ${covered} of ${total} stops along the route. MET forecasts ${horizon} days ahead and ECMWF ${extendedHorizon}; beyond that no provider has a day-specific forecast with any skill — the atmosphere is chaotic, and climate is then the most honest answer.`,

  extendedTitle: "The extended forecast for your dates",
  extendedBadge: "forecast, lower certainty",
  extendedScore: "view index, forecast",
  extendedReach: "days the model reaches",
  extendedReachNote: (nearHorizon) => `the fine-grained one reaches ${nearHorizon}`,
  extendedGrid: "km per model cell",
  extendedGridNote: (farthestKm) => `your farthest stop: ${farthestKm} km`,
  extendedDays: "of the route's days covered",
  extendedDaysAll: "the whole route is forecast",
  extendedDaysShort: (uncovered) => `${uncovered} lie beyond the model`,
  extendedDialLabel: "extended forecast",
  extendedSpan: (daysOut, nearHorizon, horizon) =>
    `Your departure is ${daysOut} days out. Days 1–${nearHorizon} have the fine-grained forecast, days ${nearHorizon + 1}–${horizon} have this one.`,
  extendedSource: (model, refinesOn) =>
    `${model} via Open-Meteo. Temperatures are elevation-corrected against each stop's own altitude. MET's fine-grained forecast takes over on ${refinesOn}.`,

  servicesTitle: "Tickets and practicalities",
  servicesTicketsNote:
    "Sells tickets for all Norwegian public transport — train, bus, boat and ferry. The same source the departures in your plan come from.",
  servicesTollsNote:
    "The toll scheme. A tag gives roughly 20 % off, and that discount is already in the cost estimate.",
  servicesRoad: "Road messages",
  servicesRoadNote:
    "Closures and convoy driving, updated through the day. Read it the same morning you cross the mountains.",
  servicesRental: (place, noun) => `${noun} in ${place}`,
  rentalNoun: { bil: "Car rental", bobil: "Motorhome rental", motorsykkel: "Motorcycle rental" },
  servicesRentalNote:
    "A search, not a recommendation: the app knows nothing about price or availability, so it names no chain.",
  servicesFoot:
    "The links go to the official services and have been checked. Car rental is a search — the app earns nothing from any of them.",

  connectionFrom: (station) => `from ${station}`,
  connectionLater: "Later:",

  planSideLabel: "Trip reference",
  foldWeather: "Weather and timing",
  foldCost: "Cost and packing",
  foldShare: "Share and listen",

  splitTitle: "Split the travel costs",
  splitLead: (range) =>
    `Fuel, tolls and ferries come to ${range} for the car — however many are sitting in it. That is the part that gets cheaper with company.`,
  splitSeats: (seats) => `${seats} in the car`,
  splitAlone: "alone",
  splitYours: "your party",
  splitNote:
    "Beds, food and entry fees are per person and do not get cheaper for a full car. The split covers what the car costs, nothing else.",

  shareTitle: "Share the trip",
  shareNameLabel: "Your name",
  shareLinkLabel: "Link to the trip",
  shareNamePlaceholder: "First name",
  sharedEdit: "Change this trip",
  shareStart: "Share this trip",
  shareCopy: "Copy link",
  shareCopied: "Link copied",
  shareCopyFailed: "Could not copy — select the link and copy it yourself",
  shareHint:
    "The link holds the whole trip: the route, the days, the date and the stops you added or removed. Whoever opens it sees what you see, and can change their own copy.",
  tripOwnedBy: (name) => `${name}'s trip`,
  sharedByLead: (name) => `You are looking at ${name}'s trip.`,
  sharedByNote:
    "Change anything and you change your own copy, not theirs. Send your new link back if you want to show what you altered.",
};

/**
 * Ordbøkene, og de tre som ikke følger med i første last.
 *
 * ── Hvorfor ───────────────────────────────────────────────────────────
 *
 * `de`, `fr` og `es` er til sammen 140 kB kildetekst, og en bruker trenger
 * høyst én av dem. Statisk importert havnet alle tre i hovedbunten, som må
 * lastes ned, tolkes og kjøres før noe som helst vises. Målt i PageSpeed på
 * mobil: Total Blocking Time 720 ms.
 *
 * ── Hvorfor de likevel er oppslag og ikke løfter ───────────────────────
 *
 * `dictionaries[lang]` leses av 77 filer, synkront, midt i en render. Å gjøre
 * det til noe man venter på ville rørt hver eneste av dem. I stedet peker de
 * tre på `en` til de er hentet, og byttes ut i objektet når de er det — da er
 * det bare én ting som må skje til: at det tegnes på nytt. Se `loadDictionary`.
 *
 * Følgen én gang per besøk, for den som har valgt tysk, fransk eller spansk:
 * grensesnittet står på engelsk i et øyeblikk. Det er byttet mot at *alle*
 * slipper å laste ned tre språk de ikke leser — og engelsk er den samme
 * reserven appen alt bruker for innholdet på de tre språkene. Se `contentLang`.
 */
export const dictionaries: Record<UiLang, Dictionary> = { no, en, de: en, fr: en, es: en };

/** Språkene som hentes for seg. `no` og `en` er alltid med. */
const LAZY: Partial<Record<UiLang, () => Promise<Dictionary>>> = {
  de: () => import("./lang/de").then((module) => module.de),
  fr: () => import("./lang/fr").then((module) => module.fr),
  es: () => import("./lang/es").then((module) => module.es),
};

const loaded = new Set<UiLang>();

/**
 * Henter ordboka for et språk, om den ikke alt er her.
 *
 * Returnerer `true` bare når noe faktisk *endret seg*, så kalleren vet om det
 * må tegnes på nytt. For `no` og `en` er svaret alltid `false`: de var her hele
 * tiden, og en tilstandsendring der ville vært en render uten grunn.
 */
export async function loadDictionary(lang: UiLang): Promise<boolean> {
  const load = LAZY[lang];
  if (!load || loaded.has(lang)) return false;
  try {
    dictionaries[lang] = await load();
    loaded.add(lang);
    return true;
  } catch {
    /*
     * Stille, og engelsk blir stående. Et nettverksbrudd midt i et
     * språkbytte skal ikke gi et tomt grensesnitt — det skal gi et
     * grensesnitt på et annet språk enn man ba om, som er langt mildere.
     */
    return false;
  }
}

/**
 * Språkene i velgeren, etter besøkstall til Norge.
 *
 * Norsk og engelsk først fordi de er de to fullstendige — de er de eneste der
 * også *innholdet* finnes oversatt. Deretter Tyskland, som er det klart største
 * utenlandsmarkedet, så Frankrike og Spania.
 */
export const LANGUAGES: { id: UiLang; label: string }[] = [
  { id: "no", label: "Norsk" },
  { id: "en", label: "English" },
  { id: "de", label: "Deutsch" },
  { id: "fr", label: "Français" },
  { id: "es", label: "Español" },
];

/** Språkene stedsbeskrivelsene finnes på. Se `UiLang` i types.ts. */
const CONTENT_LANGS: Lang[] = ["no", "en"];

const UI_LANGS: UiLang[] = LANGUAGES.map((l) => l.id);

const STORAGE_KEY = "reise-planer-lang";

/**
 * Innholdsspråket som hører til et grensesnittspråk.
 *
 * Stoppbeskrivelsene, attraksjonsnotatene og advarslene finnes bare på norsk og
 * engelsk — de er redaksjonell prosa, ikke grensesnittekst, og API-et kan bare
 * be om et språk det har. En tysk, fransk eller spansk bruker får derfor engelsk
 * innhold i et grensesnitt på sitt eget språk.
 *
 * Dette er stedet den avgjørelsen bor. Uten den ville hvert API-kall måttet
 * huske den samme oversettelsen, og ett glemt sted ville gitt en 400 fra
 * serveren for et språk den ikke kjenner.
 */
export function contentLang(lang: UiLang): Lang {
  return (CONTENT_LANGS as string[]).includes(lang) ? (lang as Lang) : "en";
}

/** Om et språk har innholdet oversatt, ikke bare grensesnittet. */
export function hasTranslatedContent(lang: UiLang): boolean {
  return (CONTENT_LANGS as string[]).includes(lang);
}

function isUiLang(value: unknown): value is UiLang {
  return typeof value === "string" && (UI_LANGS as string[]).includes(value);
}

/**
 * Finner språket: URL-parameter først, så lagret valg, så nettleserens
 * innstilling. Norsk er standard bare når nettleseren faktisk ber om det.
 *
 * Nettleserspråket leses på de to første tegnene — «de-AT», «fr-CA» og
 * «es-419» skal alle treffe. Ukjente språk faller til engelsk framfor norsk:
 * en italiensk nettleser er et sikrere tegn på at brukeren *ikke* leser norsk
 * enn på at de gjør det.
 */
export function detectLang(search: string): UiLang {
  const fromUrl = new URLSearchParams(search).get("lang");
  if (isUiLang(fromUrl)) return fromUrl;

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isUiLang(stored)) return stored;
  } catch {
    // Privat nettleservindu eller blokkerte informasjonskapsler — bruk språket i nettleseren.
  }

  const browser = navigator.language?.toLowerCase().slice(0, 2);
  if (browser === "nb" || browser === "nn" || browser === "no") return "no";
  return isUiLang(browser) ? browser : "en";
}

export function rememberLang(lang: UiLang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // Ikke kritisk; valget gjelder fortsatt for denne økten.
  }
}
